/**
 * Caddy Cart - WordPress Interactivity API Implementation
 *
 * Implements the cart store using WordPress native Interactivity API
 * with optimistic updates and WooCommerce Store API integration.
 *
 * @since 2.1.3
 */


import { store, getContext } from '@wordpress/interactivity';

// Import shared utilities
import { formatPrice, formatPriceSmart, convertStoreApiPrice, roundToTwo } from './core/shared/formatters.js';
import { getCaddyNonce, getCaddyRestUrl, getStoreApiNonce, fetchCart, refreshNonceFromResponse } from './core/shared/api.js';
import { convertStoreApiItemToCaddyFormat, convertCartItems } from './core/shared/converters.js';
import { getWooCommercePlaceholderImage, decodeHTMLEntitiesDeep } from './core/shared/dom-utils.js';
import { handlers } from './handlers/index.js';

// Flag to prevent duplicate recommendations updates during moveToCart
// Now handled via window._caddyMovingToCart in SFL module

// Track if initial cart load has completed
let initialCartLoadComplete = false;

// Track previous subtotal for reactive rewards meter (avoid redundant DOM updates)
let prevRewardsMeterSubtotal;
let prevRewardsMeterConfettiSubtotal = null;
let confettiLoadPromise = null;

function getDrawerConfettiOrigin() {
	// Prefer the visible cart drawer panel so confetti launches over Caddy.
	const drawer = document.querySelector('.cc-window, .cc-cart-window, .cc-content-wrap');
	if (!drawer) {
		return { x: 0.85, y: 0.2 };
	}

	const rect = drawer.getBoundingClientRect();
	const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 1;
	const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;

	const x = Math.min(0.98, Math.max(0.02, (rect.left + rect.width / 2) / viewportWidth));
	// Launch near the top of the drawer, not center of screen.
	const y = Math.min(0.7, Math.max(0.05, (rect.top + Math.min(rect.height * 0.2, 140)) / viewportHeight));

	return { x, y };
}

function getConfettiScriptUrls() {
	const urls = [];
	const existingConfettiScript = document.querySelector('script[src*="confetti.js"]');
	if (existingConfettiScript?.src) {
		urls.push(existingConfettiScript.src);
	}

	const premiumCoreScript = document.querySelector('script[src*="caddy-premium/public/assets/js/caddy.js"]');
	if (premiumCoreScript?.src) {
		urls.push(premiumCoreScript.src.replace(/caddy\.js(\?.*)?$/, 'confetti.js$1'));
	}

	// Fallback path for standard plugin installs.
	urls.push(`${window.location.origin}/wp-content/plugins/caddy-premium/public/assets/js/confetti.js`);
	return [...new Set(urls)];
}

function ensureConfettiLoaded() {
	if (typeof window.confetti === 'function') {
		return Promise.resolve(window.confetti);
	}

	if (confettiLoadPromise) {
		return confettiLoadPromise;
	}

	const confettiUrls = getConfettiScriptUrls();
	confettiLoadPromise = new Promise((resolve) => {
		const tryLoad = (index) => {
			if (typeof window.confetti === 'function') {
				resolve(window.confetti);
				return;
			}
			if (index >= confettiUrls.length) {
				resolve(null);
				return;
			}

			const script = document.createElement('script');
			script.src = confettiUrls[index];
			script.async = true;
			script.onload = () => resolve(typeof window.confetti === 'function' ? window.confetti : null);
			script.onerror = () => tryLoad(index + 1);
			document.head.appendChild(script);
		};
		tryLoad(0);
	});

	return confettiLoadPromise;
}

// Listen for postMessage from Customizer to open cart (must be early, before DOMContentLoaded)
window.addEventListener('message', function(event) {
	// Only accept messages from same origin (Customizer iframe)
	if (event.origin !== window.location.origin) {
		return;
	}

	if (event.data === 'open_caddy_cart') {
		// Open the cart when Customizer section is expanded
		// Use setTimeout to ensure store is available
		setTimeout(() => {
			const { actions } = store('caddy/cart');
			if (actions && actions.openCart) {
				actions.openCart();
			}
		}, 100);
	}
});

// Define the cart store

const cartStore = store('caddy/cart', {
	// State is provided by wp_interactivity_state() in PHP
	state: {
		// isLoading is writable state from PHP initial state - no getter needed
		get savedItemsCount() {
			const cartState = store('caddy/cart').state;
			return (cartState.savedItems && cartState.savedItems.length) || 0;
		},
		// Recommendations slider state getters
		get recommendationTransform() {
			const cartState = store('caddy/cart').state;
			const index = cartState.recommendationIndex || 0;
			const dragOffset = cartState._dragOffset || 0;
			// Each slide is 100% width, so translate by -100% per slide, plus any drag offset in px
			if (dragOffset !== 0) {
				return `translateX(calc(-${index * 100}% + ${dragOffset}px))`;
			}
			return `translateX(-${index * 100}%)`;
		},
		get recommendationSliderWidth() {
			// Width is handled by flex layout, no explicit width needed
			return 'auto';
		},
		get isFirstRecommendation() {
			const cartState = store('caddy/cart').state;
			const index = cartState.recommendationIndex || 0;
			return index === 0;
		},
		get isLastRecommendation() {
			const cartState = store('caddy/cart').state;
			const index = cartState.recommendationIndex || 0;
			const totalSlides = (cartState.recommendations && cartState.recommendations.length) || 0;
			return index >= totalSlides - 1;
		}
	},


	// Actions for cart interactions
	actions: {
		/**
		 * Toggle cart visibility
		 */
		async toggleCart() {
			const { state, actions } = store('caddy/cart');

			if (state.isOpen) {
				// Cart is open, close it
				actions.closeCart();
			} else {
				// Cart is closed, open it with full data refresh
				await actions.openCart();
			}
		},

		/**
		 * Open cart and refresh data from Store API
		 */
		async openCart(targetTab = 'cart', skipFetch = false) {
			const { state } = store('caddy/cart');

			// Open cart immediately - shows server-rendered content instantly
			state.isOpen = true;
			document.body.classList.add('cc-window-open');

			// Always update empty class based on current state when opening
			updateCartEmptyClass(state.cartCount);

			// Always refresh recommendations when cart opens so results match current cart contents.
			if (state.recommendations !== undefined) {
				refreshRecommendationsFromServer(state);
			} else {
				// Legacy module path
				scheduleRecommendationsPrefetch();
			}

			// Skip fetch if we already have cart data from initial load
			// Cart operations (add/remove/update) handle their own fetches
			if (!skipFetch && !initialCartLoadComplete) {
				try {
					const storeApiNonce = document.querySelector('meta[name="wc-store-api-nonce"]')?.getAttribute('content');
					const cartResponse = await fetch('/wp-json/wc/store/v1/cart', {
						headers: storeApiNonce ? { 'Nonce': storeApiNonce } : {}
					});

					// Refresh nonce from response headers
					refreshNonceFromResponse(cartResponse);

					if (cartResponse.ok) {
						const cartData = await cartResponse.json();

						// Check if cart actually changed (count or totals)
						const serverTotal = cartData.totals?.total_price || '0';
						const localTotal = String(Math.round((state.cartSubtotal || 0) * 100));
						if (state.cartCount !== cartData.items_count || serverTotal !== localTotal) {
							// Convert Store API format to Caddy format
							const caddyItems = convertCartItems(cartData.items);

							state.items = caddyItems;
							state.cartCount = cartData.items_count;
							state.isItemSingular = cartData.items_count === 1;
							updateCartTotals(state, cartData);
							updateCartEmptyClass(state.cartCount);
							updateCartWidgetCount(state.cartCount);
							state.coupons = cartData.coupons || [];


							// Update DOM elements with proper CSS classes based on sale status
							setTimeout(() => updateCartItemSaleClasses(caddyItems), 100);

							// Refresh recommendations now that cart changed
							refreshRecommendationsFromServer(state);
						}
					}
				} catch (error) {
					// Silently fail - user still sees server-rendered cart
				}
			}

			// Switch to the appropriate tab
			setTimeout(() => {
				if (targetTab === 'saves' && window._caddySwitchToSavedTab) {
					window._caddySwitchToSavedTab();
				} else if (window._caddySwitchToCartTab) {
					window._caddySwitchToCartTab();
				}
			}, 50);
		},

		/**
		 * Close cart
		 */
		closeCart() {
			const { state } = store('caddy/cart');
			state.isOpen = false;
			document.body.classList.remove('cc-window-open');
		},

		/**
		 * Header back button: close cart when on cart tab, go back to cart on other tabs
		 */
		handleHeaderBack() {
			const activeNonCart = document.querySelector('.cc-screen-tab.cc-tab-active, .cc-screen-tab.cc-active:not(#cc-cart)');
			if (activeNonCart && activeNonCart.id !== 'cc-cart') {
				// On a non-cart tab — switch back to cart
				if (window._caddySwitchToCartTab) {
					window._caddySwitchToCartTab();
				} else {
					switchToTab('#cc-cart');
				}
			} else {
				// On the cart tab — close the drawer
				store('caddy/cart').actions.closeCart();
			}
		},

		/**
		 * Navigate to previous recommendation
		 */
		prevRecommendation() {
			const { state } = store('caddy/cart');
			if (state.recommendationIndex > 0) {
				state.recommendationIndex = state.recommendationIndex - 1;
			}
		},

		/**
		 * Navigate to next recommendation
		 */
		nextRecommendation() {
			const { state } = store('caddy/cart');
			const totalSlides = (state.recommendations && state.recommendations.length) || 0;
			if (state.recommendationIndex < totalSlides - 1) {
				state.recommendationIndex = state.recommendationIndex + 1;
			}
		},

		/**
		 * Handle drag/swipe start on recommendations slider
		 */
		onSliderPointerDown(event) {
			const { state } = store('caddy/cart');
			// Only handle primary button (mouse) or touch
			if (event.button && event.button !== 0) return;
			state._isDragging = false;
			state._dragStartX = event.clientX;
			state._dragOffset = 0;

			// Disable transition during drag
			const slider = event.target.closest('.cc-pl-recommendations');
			if (slider) {
				slider.style.transition = 'none';
			}
		},

		onSliderPointerMove(event) {
			const { state } = store('caddy/cart');
			if (typeof state._dragStartX === 'undefined' || state._dragStartX === null) return;

			const diff = event.clientX - state._dragStartX;
			// Only start dragging after a small threshold to avoid interfering with clicks
			if (Math.abs(diff) > 5) {
				state._isDragging = true;
				state._dragOffset = diff;
			}
		},

		onSliderPointerUp(event) {
			const { state } = store('caddy/cart');
			if (!state._isDragging) {
				state._dragStartX = undefined;
				return;
			}

			const dragOffset = state._dragOffset || 0;
			state._dragOffset = 0;
			state._dragStartX = undefined;
			state._isDragging = false;

			// Re-enable transition for snap
			const slider = event.target.closest('.cc-pl-recommendations');
			if (slider) {
				slider.style.transition = '';
			}

			// Prevent click-through on links/buttons after a drag
			const wrapper = event.target.closest('.cc-pl-upsells-wrapper');
			if (wrapper) {
				const blocker = (e) => {
					e.preventDefault();
					e.stopPropagation();
				};
				wrapper.addEventListener('click', blocker, { capture: true, once: true });
			}

			const totalSlides = (state.recommendations && state.recommendations.length) || 0;
			// Swipe threshold: 50px
			if (dragOffset < -50 && state.recommendationIndex < totalSlides - 1) {
				state.recommendationIndex = state.recommendationIndex + 1;
			} else if (dragOffset > 50 && state.recommendationIndex > 0) {
				state.recommendationIndex = state.recommendationIndex - 1;
			}
		},

		/**
		 * Handle variation attribute selection
		 */
		selectVariation(event) {
			const { state } = store('caddy/cart');
			const select = event.target;
			const attrSlug = select.dataset.attributeSlug;
			const value = select.value;

			// Get product ID from closest slide element (preserved before nested template)
			const slideEl = select.closest('.cc-slide');
			const productId = parseInt(slideEl?.dataset?.productId);

			// Find the recommendation item in state
			const item = state.recommendations?.find(rec => rec.id === productId);

			if (!item || !item.variations) return;

			// Initialize selectedAttributes if not exists
			if (!item.selectedAttributes) {
				item.selectedAttributes = {};
			}

			// Store the selected value
			if (value) {
				item.selectedAttributes[attrSlug] = value;
			} else {
				delete item.selectedAttributes[attrSlug];
			}

			// Check if all attributes are selected
			const allSelected = item.attributes.every(attr =>
				item.selectedAttributes[attr.slug]
			);

			if (allSelected) {
				// Find matching variation
				const matchingVariation = item.variations.find(variation => {
					return Object.entries(item.selectedAttributes).every(([slug, val]) => {
						const attrKey = `attribute_${slug}`;
						const variationValue = variation.attributes[attrKey];
						// Match if variation allows any value (empty) or matches exactly
						return !variationValue || variationValue.toLowerCase() === val.toLowerCase();
					});
				});

				if (matchingVariation && matchingVariation.is_in_stock && matchingVariation.is_purchasable) {
					item.selectedVariationId = matchingVariation.variation_id;
					item.variationSelected = true;
					// Update price display
					item.price = formatPriceSmart(matchingVariation.display_price);
					item.priceHtml = formatPrice(matchingVariation.display_price);
				} else {
					item.selectedVariationId = 0;
					item.variationSelected = false;
				}
			} else {
				item.selectedVariationId = 0;
				item.variationSelected = false;
			}
		},

		/**
		 * Add recommendation to cart via Store API
		 */
		async addRecommendationToCart(event) {
			const { state } = store('caddy/cart');

			// Get product ID from closest slide element
			const button = event.target;
			const slideEl = button.closest('.cc-slide');
			const productId = parseInt(slideEl?.dataset?.productId);

			// Find the recommendation item in state
			const item = state.recommendations?.find(rec => rec.id === productId);

			if (!item || !item.id) {
				return;
			}

			// For variable products, check if variation is selected
			if (item.isVariable) {
				if (!item.selectedVariationId) {
					// Redirect to product page if no variation selected
					window.location.href = item.permalink;
					return;
				}
			}

			// Set loading state on the item
			item.isAdding = true;

			try {
				const storeApiNonce = document.querySelector('meta[name="wc-store-api-nonce"]')?.getAttribute('content');

				// Use variation ID for variable products
				const productId = item.isVariable ? item.selectedVariationId : item.id;

				const response = await fetch('/wp-json/wc/store/v1/cart/add-item', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						...(storeApiNonce ? { 'Nonce': storeApiNonce } : {})
					},
					body: JSON.stringify({
						id: productId,
						quantity: 1
					})
				});

				if (response.ok) {
					const cartData = await response.json();

					// Update cart state
					const caddyItems = convertCartItems(cartData.items);
					state.items = caddyItems;
					state.cartCount = cartData.items_count;
					state.isItemSingular = cartData.items_count === 1;
					updateCartTotals(state, cartData);
					updateCartEmptyClass(state.cartCount);
					updateCartWidgetCount(state.cartCount);
					state.coupons = cartData.coupons || [];

					// Dispatch cart update event for premium features (rewards meter, announcement bar, etc.)
					document.dispatchEvent(new CustomEvent('caddy_cart_updated', { detail: { cartData } }));

					// Refresh recommendations after adding item
					refreshRecommendationsFromServer(state);
				}
			} catch (error) {
				// Silently fail
			} finally {
				item.isAdding = false;
			}
		},

		/**
		 * Increase item quantity
		 */
		async increaseQuantity() {
			const context = getContext();
			const cartKey = context.item.cartKey;
			const currentQuantity = context.item.quantity;
			if (context.item.isAtMaxQty) return;
			const newQuantity = currentQuantity + 1;
			const { state } = store('caddy/cart');

			// Find the item index first
			const itemIndex = state.items.findIndex(item => item.cartKey === cartKey);

			// Block qty changes on fixed-qty bundled items
			if (itemIndex !== -1 && state.items[itemIndex].hideQuantityButtons) {
				return;
			}

			// Respect quantity limits (sold individually, max qty)
			if (itemIndex !== -1 && state.items[itemIndex].maxQuantity && newQuantity > state.items[itemIndex].maxQuantity) {
				return;
			}

			// Prevent rapid clicks - check if this item is already updating
			if (itemIndex !== -1 && state.items[itemIndex].isUpdating) {
				return;
			}

			// Optimistic update - update only the specific item
			if (itemIndex !== -1) {
				// Set updating flag to disable buttons
				state.items[itemIndex].isUpdating = true;
				const unitPrice = state.items[itemIndex].unitPrice;
				const newLineTotal = unitPrice * newQuantity;

				// Update quantity and displayed price
				state.items[itemIndex].quantity = newQuantity;
				state.items[itemIndex].isAtMinQty = newQuantity <= (state.items[itemIndex].minQuantity || 1);
				state.items[itemIndex].isAtMaxQty = newQuantity >= (state.items[itemIndex].maxQuantity || Infinity);
				// Delete and re-assign price to force reactivity
				delete state.items[itemIndex].price;
				state.items[itemIndex].price = formatPriceSmart(newLineTotal);
				state.items[itemIndex].priceHtml = formatPrice(newLineTotal);
				// Update the lineTotal to match what server will return
				state.items[itemIndex].lineTotal = newLineTotal;

				// Update regularPriceFormatted if item is on sale
				if (state.items[itemIndex].isOnSale && state.items[itemIndex].regularPrice) {
					const newRegularLineTotal = state.items[itemIndex].regularPrice * newQuantity;
					delete state.items[itemIndex].regularPriceFormatted;
					state.items[itemIndex].regularPriceFormatted = formatPriceSmart(newRegularLineTotal);
				}

				// Update cart count immediately (always responsive)
				state.cartCount = state.items.reduce((total, item) => total + item.quantity, 0);
				state.isItemSingular = state.cartCount === 1;
				updateCartEmptyClass(state.cartCount);
				updateCartWidgetCount(state.cartCount);

				// Check if coupons are applied
				const hasCoupons = state.coupons && state.coupons.length > 0;

				if (!hasCoupons) {
					// Only update subtotal optimistically if no coupons are applied
					// This avoids the jumping meter issue when coupons affect pricing
					const newSubtotal = state.items.reduce((total, item) => total + (item.lineTotal || 0), 0);
					state.cartSubtotal = newSubtotal;
					state.cartSubtotalDisplay = formatPriceSmart(newSubtotal);
					state.cartSubtotalFormatted = formatPrice(newSubtotal);
					state.cartTotal = formatPrice(newSubtotal);
					updateCartTotalsFromItems(state, true); // skipSubtotal = true, already calculated above
				}
				// If coupons are applied, wait for server response to get accurate totals
			}

			// Sync with server in background
			try {
				await updateQuantityOnServer(cartKey, newQuantity, itemIndex);
			} catch (error) {
				// Rollback optimistic update on error
				if (itemIndex !== -1) {
					const unitPrice = state.items[itemIndex].unitPrice;
					const rollbackLineTotal = unitPrice * currentQuantity;

					state.items[itemIndex].quantity = currentQuantity;
					state.items[itemIndex].isAtMinQty = currentQuantity <= (state.items[itemIndex].minQuantity || 1);
					state.items[itemIndex].isAtMaxQty = currentQuantity >= (state.items[itemIndex].maxQuantity || Infinity);

					// Delete and re-assign price to force reactivity
					delete state.items[itemIndex].price;
					state.items[itemIndex].price = formatPriceSmart(rollbackLineTotal);
						state.items[itemIndex].priceHtml = formatPrice(rollbackLineTotal);

					state.items[itemIndex].lineTotal = rollbackLineTotal;

					// Recalculate totals
					state.cartCount = state.items.reduce((total, item) => total + item.quantity, 0);
					state.isItemSingular = state.cartCount === 1;
					updateCartTotalsFromItems(state);
					updateCartEmptyClass(state.cartCount);
					updateCartWidgetCount(state.cartCount);
				}
			} finally {
				// Always clear the updating flag when done
				if (itemIndex !== -1) {
					state.items[itemIndex].isUpdating = false;
				}
			}
		},

		/**
		 * Decrease item quantity
		 */
		async decreaseQuantity() {
			const context = getContext();
			const cartKey = context.item.cartKey;
			const currentQuantity = context.item.quantity;
			const { state } = store('caddy/cart');

			// Block qty changes on fixed-qty bundled items
			const itemIndex = state.items.findIndex(item => item.cartKey === cartKey);
			if (itemIndex !== -1 && state.items[itemIndex].hideQuantityButtons) {
				return;
			}

			// Respect minimum quantity (defaults to 1)
			const minQty = context.item.minQuantity || 1;
			if (currentQuantity <= minQty) {
				return;
			}

			const newQuantity = currentQuantity - 1;

			// Prevent rapid clicks - check if this item is already updating
			if (itemIndex !== -1 && state.items[itemIndex].isUpdating) {
				return;
			}

			// Optimistic update - update only the specific item
			if (itemIndex !== -1) {
				// Set updating flag to disable buttons
				state.items[itemIndex].isUpdating = true;
				const unitPrice = state.items[itemIndex].unitPrice;
				const newLineTotal = unitPrice * newQuantity;

				// Update quantity and displayed price
				state.items[itemIndex].quantity = newQuantity;
				state.items[itemIndex].isAtMinQty = newQuantity <= (state.items[itemIndex].minQuantity || 1);
				state.items[itemIndex].isAtMaxQty = newQuantity >= (state.items[itemIndex].maxQuantity || Infinity);
				// Delete and re-assign price to force reactivity
				delete state.items[itemIndex].price;
				state.items[itemIndex].price = formatPriceSmart(newLineTotal);
				state.items[itemIndex].priceHtml = formatPrice(newLineTotal);
				// Update the lineTotal to match what server will return
				state.items[itemIndex].lineTotal = newLineTotal;

				// Update regularPriceFormatted if item is on sale
				if (state.items[itemIndex].isOnSale && state.items[itemIndex].regularPrice) {
					const newRegularLineTotal = state.items[itemIndex].regularPrice * newQuantity;
					delete state.items[itemIndex].regularPriceFormatted;
					state.items[itemIndex].regularPriceFormatted = formatPriceSmart(newRegularLineTotal);
				}

				// Update cart count immediately (always responsive)
				state.cartCount = state.items.reduce((total, item) => total + item.quantity, 0);
				state.isItemSingular = state.cartCount === 1;
				updateCartEmptyClass(state.cartCount);
				updateCartWidgetCount(state.cartCount);

				// Check if coupons are applied
				const hasCoupons = state.coupons && state.coupons.length > 0;

				if (!hasCoupons) {
					// Only update subtotal optimistically if no coupons are applied
					// This avoids the jumping meter issue when coupons affect pricing
					const newSubtotal = state.items.reduce((total, item) => total + (item.lineTotal || 0), 0);
					state.cartSubtotal = newSubtotal;
					state.cartSubtotalDisplay = formatPriceSmart(newSubtotal);
					state.cartSubtotalFormatted = formatPrice(newSubtotal);
					state.cartTotal = formatPrice(newSubtotal);
					updateCartTotalsFromItems(state, true); // skipSubtotal = true, already calculated above
				}
				// If coupons are applied, wait for server response to get accurate totals
			}

			// Sync with server in background
			try {
				await updateQuantityOnServer(cartKey, newQuantity, itemIndex);
			} catch (error) {
				// Rollback optimistic update on error
				if (itemIndex !== -1) {
					const unitPrice = state.items[itemIndex].unitPrice;
					const rollbackLineTotal = unitPrice * currentQuantity;

					state.items[itemIndex].quantity = currentQuantity;
					state.items[itemIndex].isAtMinQty = currentQuantity <= (state.items[itemIndex].minQuantity || 1);
					state.items[itemIndex].isAtMaxQty = currentQuantity >= (state.items[itemIndex].maxQuantity || Infinity);

					// Delete and re-assign price to force reactivity
					delete state.items[itemIndex].price;
					state.items[itemIndex].price = formatPriceSmart(rollbackLineTotal);
						state.items[itemIndex].priceHtml = formatPrice(rollbackLineTotal);

					state.items[itemIndex].lineTotal = rollbackLineTotal;

					// Recalculate totals
					state.cartCount = state.items.reduce((total, item) => total + item.quantity, 0);
					state.isItemSingular = state.cartCount === 1;
					updateCartTotalsFromItems(state);
					updateCartEmptyClass(state.cartCount);
					updateCartWidgetCount(state.cartCount);
				}
			} finally {
				// Always clear the updating flag when done
				if (itemIndex !== -1) {
					state.items[itemIndex].isUpdating = false;
				}
			}
		},

		/**
		 * Remove item from cart
		 */
		async removeItem() {
			const context = getContext();
			const cartKey = context.item.cartKey;
			const { state } = store('caddy/cart');

			const itemIndex = state.items.findIndex(item => item.cartKey === cartKey);
			if (itemIndex === -1) return;

			const removedItem = state.items[itemIndex];

			// Optimistic update
			state.items.splice(itemIndex, 1);

			// Bundle container: also remove its bundled children
			if (removedItem.isBundleContainer) {
				for (let i = state.items.length - 1; i >= 0; i--) {
					if (state.items[i].bundledBy === cartKey) {
						state.items.splice(i, 1);
					}
				}
			}

			state.cartCount = state.items.reduce((total, item) => total + item.quantity, 0);
			state.isItemSingular = state.cartCount === 1;
			updateCartEmptyClass(state.cartCount);
			updateCartWidgetCount(state.cartCount);
			const rawSubtotal = state.items.reduce((total, item) => total + item.lineTotal, 0);
			state.cartSubtotal = Math.round(rawSubtotal * 100) / 100;
			updateCartTotalsFromItems(state, true);

			// Sync with server — serialize mutations to prevent 409 Conflict errors.
			// WooCommerce Store API rejects concurrent cart writes.
			pendingRemovals.add(cartKey);
			try {
				await queueCartMutation(() => removeItemFromServer(cartKey));
				pendingRemovals.delete(cartKey);
			} catch (error) {
				pendingRemovals.delete(cartKey);
				console.error('ERROR in removeItem:', error, {
					cartKey, itemName: removedItem.name
				});
				// Rollback by refreshing true server state
				await refreshCartFromServer();
			} finally {
				state.isLoading = false;
			}
		},

		/**
		 * WCS-ATT: Upgrade to subscription (click handler for upgrade button)
		 */
		async upgradeToSubscription() {
			const context = getContext();
			const item = context.item;
			const { state } = store('caddy/cart');

			if (item.subscriptionUpdating) return;
			if (!item.subscriptionOptions || item.subscriptionOptions.length === 0) return;

			// Find first subscription option (non-zero value)
			const firstSub = item.subscriptionOptions.find(opt => opt.value !== '0');
			if (!firstSub) return;

			// Toggle visibility
			item.showUpgradeButton = false;
			item.showSubscriptionDropdown = true;

			// Update selected state
			item.subscriptionOptions.forEach(opt => {
				opt.selected = (opt.value === firstSub.value);
			});

			// Send AJAX to update subscription scheme
			await updateSubscriptionScheme(item, firstSub.value, state);
		},

		/**
		 * WCS-ATT: Change subscription scheme (dropdown change handler)
		 */
		async changeSubscriptionScheme(event) {
			const context = getContext();
			const item = context.item;
			const { state } = store('caddy/cart');
			const schemeKey = event.target.value;

			if (item.subscriptionUpdating) return;

			// Update selected state
			item.subscriptionOptions.forEach(opt => {
				opt.selected = (opt.value === schemeKey);
			});

			// Send AJAX first, then toggle visibility after completion
			const switchingToOneTime = (schemeKey === '0');
			await updateSubscriptionScheme(item, schemeKey, state, switchingToOneTime);
		},

		/**
		 * Update cart totals
		 */
		updateTotals() {
			const { state } = store('caddy/cart');

			// Recalculate cart total
			const total = state.items.reduce((sum, item) => sum + item.lineTotal, 0);
			state.cartTotal = formatPrice(total);
			state.cartSubtotal = total;
			state.cartSubtotalDisplay = formatPriceSmart(total);
			state.cartSubtotalFormatted = formatPrice(total);
			state.cartCount = state.items.reduce((total, item) => total + item.quantity, 0);
			state.isItemSingular = state.cartCount === 1;
		}

		// Save for Later actions (saveForLater, removeSavedItem, moveToCart) are added dynamically
		// by the SFL module when state.saveForLaterEnabled is true
	},

	// Callbacks for lifecycle events and debugging
	callbacks: {

		/**
		 * Initialize cart when DOM is ready
		 */
		async init() {
			const { state } = store('caddy/cart');
			const context = getContext();

			// Schedule background prefetch for recommendations
			scheduleRecommendationsPrefetch();

			// Auto-open cart if configured and items were just added
			if (context && context.autoOpen && state.cartCount > 0) {
				// Check if this is likely a fresh add-to-cart action
				const urlParams = new URLSearchParams(window.location.search);
				if (urlParams.get('add-to-cart')) {
					setTimeout(() => {
						store('caddy/cart').actions.openCart();
					}, 100);
				}
			}

			// Listen for WooCommerce cart updates
			document.addEventListener('wc_cart_emptied', () => {
				state.items = [];
				state.cartCount = 0;
				state.cartTotal = formatPrice(0);
				state.cartSubtotal = 0;
				state.cartSubtotalDisplay = formatPriceSmart(0);
				state.cartSubtotalFormatted = formatPrice(0);
				updateCartWidgetCount(state.cartCount);
			});

			// Global click handler for shortcode widgets and header icon tabs
			document.addEventListener('click', (e) => {
				const cartWidget = e.target.closest('.cc_cart_items_list');
				if (cartWidget) {
					e.preventDefault();
					store('caddy/cart').actions.openCart();
					return;
				}
				const savedWidget = e.target.closest('.cc_saved_items_list');
				if (savedWidget) {
					e.preventDefault();
					store('caddy/cart').actions.openCart('saves');
					return;
				}
				// Offers tab click in header
				const offersNav = e.target.closest('.cc-offers-nav');
				if (offersNav) {
					e.preventDefault();
					switchToTab('#cc-offers');
					return;
				}
			});

			// Listen for add to cart events
			document.addEventListener('wc_add_to_cart', async () => {
				// Skip if this is from moveToCart or our own handlers (they manage their own updates)
				if (window._caddyMovingToCart || window._caddyAddingToCart) {
					return;
				}

				try {
					// Refresh cart from WooCommerce Store API
					await refreshCartFromServer();

					// Update recommendations with new cart items (only if cart has items)
					const { state } = store('caddy/cart');
					if (state.items && state.items.length > 0) {
						refreshRecommendationsFromServer(state);
					}
				} catch (error) {
				}
			});

			// Handle escape key
			document.addEventListener('keydown', (event) => {
				if (event.key === 'Escape' && state.isOpen) {
					store('caddy/cart').actions.closeCart();
				}
			});

			// Intercept add-to-cart forms to prevent page refresh (single product and shop pages)
			// Match: form.cart, variations_form class, or any form with add-to-cart/product_id inputs
			const addToCartForms = document.querySelectorAll('form.cart, form.variations_form, .woocommerce form[action*="add-to-cart"], form:has(input[name="add-to-cart"]), form:has(input[name="product_id"])');
			const addToCartButtons = document.querySelectorAll('a.add_to_cart_button, .wc-block-grid__product-add-to-cart a');

			// Handle traditional forms (single product pages) - use capture phase and high priority
			addToCartForms.forEach(form => {
				// Mark form as handled by Interactivity API to prevent duplicate DOMContentLoaded handler
				form.dataset.caddyIntercepted = 'true';

				// Remove any existing onsubmit handlers
				form.onsubmit = null;

				// Override the submit method to prevent any programmatic submission
				const originalSubmit = form.submit;
				form.submit = function() {
					return false;
				};

				// Use capture phase to intercept before other handlers
				form.addEventListener('submit', async (event) => {
					event.preventDefault();
					event.stopImmediatePropagation();

					const formData = new FormData(form);
					// Submit button values aren't included in FormData, so fall back to the button's value attribute
					const addToCartBtn = form.querySelector('button[name="add-to-cart"], input[name="add-to-cart"]');
					const productId = formData.get('add-to-cart') || formData.get('product_id') || (addToCartBtn && addToCartBtn.value);
					const quantity = parseInt(formData.get('quantity')) || 1;
					const variation = formData.get('variation_id') || '';

					// Add loading state
					state.isLoading = true;
					const submitBtn = form.querySelector('[type="submit"]');
					const originalText = submitBtn ? submitBtn.textContent : '';
					if (submitBtn) {
						submitBtn.textContent = state.i18n.adding;
						submitBtn.disabled = true;
					}

					// Show spinner on compass
					const compassIcon = document.querySelector('.cc-compass .licon');
					if (compassIcon) {
						compassIcon.classList.add('cc-loading');
					}

					try {
						const variationInt = parseInt(variation) || 0;

						// Build base Store API payload
						// For variable products (including variation bundles), use the variation ID.
						// For regular bundles (no top-level variation), use the parent product ID.
						const addToCartData = {
							id: variationInt > 0 ? variationInt : parseInt(productId),
							quantity: parseInt(quantity)
						};

						// Variable product attributes
						if (variationInt > 0) {
							const attributes = [];
							for (const [key, value] of formData.entries()) {
								if (key.startsWith('attribute_')) {
									attributes.push({
										attribute: key.replace('attribute_', ''),
										value: value
									});
								}
							}
							if (attributes.length > 0) {
								addToCartData.variation = attributes;
							}
						}

						// Let product-type handlers contribute to API data
						const matchedHandlers = handlers.filter(h => h.matches(form, formData));
						for (const handler of matchedHandlers) {
							const extra = handler.buildApiData(form, formData, productId);
							if (extra) Object.assign(addToCartData, extra);
						}

						const storeApiUrl = `${window.location.origin}/wp-json/wc/store/v1/cart/add-item`;

						// Get Store API nonce
						let storeApiNonce = null;
						const storeApiNonceMeta = document.querySelector('meta[name="wc-store-api-nonce"]');
						if (storeApiNonceMeta) {
							storeApiNonce = storeApiNonceMeta.getAttribute('content');
						}

						const headers = {
							'Content-Type': 'application/json',
						};

						if (storeApiNonce) {
							headers['Nonce'] = storeApiNonce;
						}

						const response = await fetch(storeApiUrl, {
							method: 'POST',
							credentials: 'same-origin',
							headers: headers,
							body: JSON.stringify(addToCartData)
						});


						if (response.ok) {
							// Store API add-item already returns the full cart - no need for second request!
							const cartData = await response.json();
							const cartStore = store('caddy/cart');

							// Convert Store API format to Caddy format
							const caddyItems = convertCartItems(cartData.items);

							if (pendingRemovals.size > 0) {
							}
							cartStore.state.items = caddyItems;
							cartStore.state.cartCount = cartData.items_count;
							cartStore.state.isItemSingular = cartData.items_count === 1;
							updateCartTotals(cartStore.state, cartData);
							updateCartEmptyClass(cartStore.state.cartCount);
							updateCartWidgetCount(cartStore.state.cartCount);
							cartStore.state.cartHash = cartData.cartHash || 'updated';
							cartStore.state.coupons = cartData.coupons || [];
							cartStore.state.discountTotal = getStoreApiDiscountTotal(cartStore.state, cartData);

							// Update applied coupons display to refresh discount amount
							updateAppliedCouponsDisplay(cartData.coupons || []);

							// Dispatch cart update event for premium features (rewards meter, etc.)
							document.dispatchEvent(new CustomEvent('caddy_cart_updated', { detail: { cartData } }));

							// Trigger WooCommerce add to cart event for other plugins
							window._caddyAddingToCart = true;
							document.dispatchEvent(new CustomEvent('wc_add_to_cart', {
								detail: { productId, quantity, variation }
							}));
							window._caddyAddingToCart = false;

							// Run post-add handlers (WCS-ATT, bundle-sells, etc.)
							for (const handler of matchedHandlers) {
								if (handler.afterAddToCart) {
									await handler.afterAddToCart(form, formData, cartData, {
										refreshCartFromServer, refreshRecommendationsFromServer, state,
										storeApiUrl, headers
									});
								}
							}

							// Auto-open cart after successful add
							setTimeout(() => {
								store('caddy/cart').actions.openCart('cart', true);
							}, 100);

							// Show success feedback
							if (submitBtn) {
								submitBtn.textContent = state.i18n.added;
								setTimeout(() => {
									if (submitBtn) submitBtn.textContent = originalText;
								}, 1500);
							}
						} else {
							// Parse error message from Store API JSON response
							let errorMsg = '';
							try {
								const errorJson = await response.json();
								errorMsg = errorJson.message || '';
							} catch (e) {
								errorMsg = '';
							}
							throw new Error(errorMsg || `Store API returned status: ${response.status}`);
						}
					} catch (error) {
						// Show WooCommerce-style error notice on the page
						const errorMsg = error.message || state.i18n.errorTryAgain;
						const noticesWrapper = form.closest('.product')?.querySelector('.woocommerce-notices-wrapper')
							|| document.querySelector('.woocommerce-notices-wrapper');
						if (noticesWrapper) {
							while (noticesWrapper.firstChild) noticesWrapper.removeChild(noticesWrapper.firstChild);
							const notice = document.createElement('ul');
							notice.className = 'woocommerce-error';
							notice.setAttribute('role', 'alert');
							const li = document.createElement('li');
							li.textContent = errorMsg.replace(/&quot;/g, '"').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&#039;/g, "'");
							notice.appendChild(li);
							noticesWrapper.appendChild(notice);
							noticesWrapper.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
						}
						if (submitBtn) {
							submitBtn.textContent = originalText;
						}
					} finally {
						// Reset button state
						state.isLoading = false;
						if (submitBtn) {
							submitBtn.disabled = false;
						}
					}

					// Always prevent form submission
					return false;
				}, true); // Use capture phase
			});

			// Handle shop page add-to-cart buttons
			addToCartButtons.forEach(button => {
				// Mark button as handled by Interactivity API to prevent duplicate DOMContentLoaded handler
				button.dataset.caddyIntercepted = 'true';

				button.addEventListener('click', async (event) => {
					// Skip variable and grouped product buttons - let them navigate to product page
					if (button.classList.contains('product_type_variable') ||
					    button.classList.contains('product_type_grouped')) {
						return; // Don't prevent default, allow normal navigation
					}

					// Skip "Read more" buttons (non-purchasable products)
					if (!button.classList.contains('add_to_cart_button')) {
						return; // Don't prevent default, allow normal navigation
					}

					event.preventDefault();
					event.stopPropagation();

					const productId = button.dataset.product_id || button.getAttribute('data-product_id');
					const quantity = button.dataset.quantity || 1;

					if (!productId) {
						return;
					}


					// Add loading state — use CSS classes only to preserve theme icons/markup
					state.isLoading = true;
					button.classList.add('loading');
					button.disabled = true;

					try {
						// Use Store API add-item for consistent, fast performance
						const storeApiUrl = `${window.location.origin}/wp-json/wc/store/v1/cart/add-item`;

						// Get Store API nonce
						let storeApiNonce = null;
						const storeApiNonceMeta = document.querySelector('meta[name="wc-store-api-nonce"]');
						if (storeApiNonceMeta) {
							storeApiNonce = storeApiNonceMeta.getAttribute('content');
						}

						const headers = {
							'Content-Type': 'application/json',
						};

						if (storeApiNonce) {
							headers['Nonce'] = storeApiNonce;
						}

						const response = await fetch(storeApiUrl, {
							method: 'POST',
							credentials: 'same-origin',
							headers: headers,
							body: JSON.stringify({
								id: productId,
								quantity: parseInt(quantity)
							})
						});

						if (response.ok) {
							// Store API add-item already returns the full cart - use it!
							const cartData = await response.json();
							const cartStore = store('caddy/cart');

							// Convert Store API format to Caddy format
							const caddyItems = convertCartItems(cartData.items);

							cartStore.state.items = caddyItems;
							cartStore.state.cartCount = cartData.items_count;
							cartStore.state.isItemSingular = cartData.items_count === 1;
							updateCartTotals(cartStore.state, cartData);
							updateCartEmptyClass(cartStore.state.cartCount);
							updateCartWidgetCount(cartStore.state.cartCount);
							cartStore.state.cartHash = cartData.cartHash || 'updated';
							cartStore.state.coupons = cartData.coupons || [];
							cartStore.state.discountTotal = getStoreApiDiscountTotal(cartStore.state, cartData);

							// Update applied coupons display
							updateAppliedCouponsDisplay(cartData.coupons || []);

							// Dispatch cart update event for premium features (rewards meter, etc.)
							document.dispatchEvent(new CustomEvent('caddy_cart_updated', { detail: { cartData } }));

							// Trigger WooCommerce add to cart event for compatibility
							window._caddyAddingToCart = true;
							document.dispatchEvent(new CustomEvent('wc_add_to_cart', {
								detail: { productId, quantity }
							}));
							window._caddyAddingToCart = false;

							// Auto-open cart after successful add (skip fetch - we already have fresh data!)
							setTimeout(() => {
								store('caddy/cart').actions.openCart('cart', true);
							}, 100);

							// Show success via CSS class — preserves theme icons/markup
							button.classList.remove('loading');
							button.classList.add('added');
							setTimeout(() => {
								button.classList.remove('added');
							}, 1500);
						} else {
							const { state: cartState } = store('caddy/cart');
							throw new Error(cartState.i18n.errorAddToCartFailed);
						}
					} catch (error) {
						// Fallback to original link behavior
						window.location.href = button.href;
					} finally {
						// Reset button state
						state.isLoading = false;
						button.classList.remove('loading');
						button.disabled = false;
					}
				});
			});
		},

		/**
		 * Debug pricing element to see what Interactivity API is doing
		 */
		/**
		 * Reactive rewards meter — runs whenever state.cartSubtotal or state.coupons change.
		 * Bound via data-wp-watch on the rewards meter container.
		 */
		updateRewardsMeter() {
			const { state } = store('caddy/cart');
			const premiumState = store('caddy/premium');

			// Read reactive state so Interactivity API tracks dependencies
			const cartSubtotal = state.cartSubtotal;
			const coupons = state.coupons;

			// Skip if rewards meter not enabled
			if (!premiumState?.state?.rewardsMeter?.enabled) return;
			const tiers = premiumState.state.rewardsMeter.tiers;
			if (!tiers) return;

			// Get enabled tiers sorted by threshold
			const enabledTiers = [];
			for (const [tierId, settings] of Object.entries(tiers)) {
				if (settings.enabled && settings.threshold > 0) {
					enabledTiers.push({ id: tierId, ...settings });
				}
			}
			if (!enabledTiers.length) return;
			enabledTiers.sort((a, b) => a.threshold - b.threshold);

			// Calculate meter subtotal for threshold comparison.
			// cartSubtotal already has ALL coupon discounts subtracted (including rewards).
			// We must add back reward coupon discounts so thresholds don't oscillate
			// (discount lowers total → threshold unmet → discount removed → threshold met → loop).
			// Optionally subtract non-reward coupons based on admin setting.
			const calculateBeforeCoupon = premiumState?.state?.settings?.couponDiscountsEnabled || false;
			let rewardDiscount = 0;
			let nonRewardDiscount = 0;
			if (coupons?.length) {
				coupons.forEach(coupon => {
					const discount = convertStoreApiPrice(coupon.totals?.total_discount || 0);
					if (coupon.code.includes('dollar-reward') || coupon.code.includes('%-reward')) {
						rewardDiscount += discount;
					} else if (!calculateBeforeCoupon) {
						nonRewardDiscount += discount;
					}
				});
			}
			const meterSubtotal = cartSubtotal + rewardDiscount - nonRewardDiscount;
			const previousMeterSubtotal = prevRewardsMeterConfettiSubtotal;

			// Track previous subtotal to avoid redundant DOM updates
			if (prevRewardsMeterSubtotal === meterSubtotal) return;
			prevRewardsMeterSubtotal = meterSubtotal;

			// Update meter bars
			const meterContainers = document.querySelectorAll('.cc-rewards-meter-container');
			const tierMarkers = document.querySelectorAll('.tier-marker');
			let prevThreshold = 0;

			meterContainers.forEach((container, i) => {
				const meter = container.querySelector('.cc-fs-meter-used');
				if (!meter) return;

				const threshold = parseFloat(container.dataset.tierThreshold || 0);
				const range = threshold - prevThreshold;
				let progress = 0;

				if (range > 0) {
					if (meterSubtotal <= prevThreshold) progress = 0;
					else if (meterSubtotal >= threshold) progress = 100;
					else progress = ((meterSubtotal - prevThreshold) / range) * 100;
				} else {
					progress = meterSubtotal >= threshold ? 100 : 0;
				}

				meter.style.width = progress + '%';

				const isActive = meterSubtotal >= threshold;
				const tierName = container.dataset.tierName || '';
				meter.classList.remove('tier1-active', 'tier2-active', 'tier3-active');

				if (tierMarkers[i]) {
					tierMarkers[i].classList.toggle('active', isActive);
					if (isActive && tierName) meter.classList.add(tierName + '-active');
				}

				prevThreshold = threshold;
			});

			// Update last marker
			const lastMarker = tierMarkers[tierMarkers.length - 1];
			if (lastMarker && meterContainers.length > 0) {
				const lastContainer = meterContainers[meterContainers.length - 1];
				const lastThreshold = parseFloat(lastContainer.dataset.tierThreshold || 0);
				lastMarker.classList.toggle('active', meterSubtotal >= lastThreshold);
			}

			// Update message
			const msgEl = document.querySelector('.cc-rewards-meters-container .cc-fs-title');
			if (msgEl) {
				const i18n = store('caddy/cart')?.state?.i18n || {};
				let nextTier = null;
				for (const tier of enabledTiers) {
					if (meterSubtotal < tier.threshold) { nextTier = tier; break; }
				}

				if (!nextTier) {
					msgEl.textContent = i18n.allRewardsUnlocked || 'All rewards unlocked!';
				} else {
					const remaining = Math.max(nextTier.threshold - meterSubtotal, 0);
					let msg = nextTier.text || i18n.spendMoreUnlockReward || 'Spend {amount} more to unlock your reward';
					if (!nextTier.text && nextTier.reward_type === 'free_shipping') {
						msg = i18n.spendMoreFreeShipping || 'Spend {amount} more to get free shipping';
					}
					msg = msg.replace(/{amount}/g, formatPrice(remaining));
					msgEl.textContent = msg;
				}
			}

			// Trigger confetti only when crossing a tier threshold upward.
			enabledTiers.forEach(tier => {
				const tierConfettiEnabled = tier.confetti === 'enabled' || tier.confetti === true || tier.confetti === 'true' || tier.confetti === 1 || tier.confetti === '1' || tier.confetti === 'on';
				if (
					tierConfettiEnabled &&
					previousMeterSubtotal !== null &&
					previousMeterSubtotal < tier.threshold &&
					meterSubtotal >= tier.threshold
				) {
					const confettiFn = window.confetti || (typeof confetti === 'function' ? confetti : null);
					if (typeof confettiFn === 'function') {
						confettiFn({ particleCount: 100, spread: 70, origin: getDrawerConfettiOrigin(), zIndex: 999999 });
					} else {
						ensureConfettiLoaded().then((loadedConfettiFn) => {
							if (typeof loadedConfettiFn === 'function') {
								loadedConfettiFn({ particleCount: 100, spread: 70, origin: getDrawerConfettiOrigin(), zIndex: 999999 });
							} else {
								document.dispatchEvent(new CustomEvent('caddy_reward_unlocked', {
									detail: { showConfetti: true, tierId: tier.id }
								}));
							}
						});
					}
				}
			});

			prevRewardsMeterConfettiSubtotal = meterSubtotal;
		},

		debugPricing() {
			const { state } = store('caddy/cart');
			const context = getContext();
		},

		/**
		 * Debug savings element to see what Interactivity API is doing
		 */
		debugSavings() {
			const context = getContext();
		},

		/**
		 * Debug cart item to see if data-wp-each is working
		 */
		debugCartItem() {
			const context = getContext();
		}
	}
});

// Simple, direct form and button interception for single product and shop pages
document.addEventListener('DOMContentLoaded', function() {
	// Handle single product page forms - match all WooCommerce add-to-cart forms
	const forms = document.querySelectorAll('form.cart, form.variations_form, form:has(input[name="add-to-cart"]), form:has(input[name="product_id"])');

	forms.forEach(form => {
		form.addEventListener('submit', async function(e) {
			// Skip if already handled by the Interactivity API init callback
			if (form.dataset.caddyIntercepted === 'true') {
				return;
			}

			e.preventDefault();

			const formData = new FormData(form);
			// Get product ID from the submit button's value attribute
			const productId = form.querySelector('button[name="add-to-cart"]')?.value ||
							  formData.get('add-to-cart') ||
							  formData.get('product_id') ||
							  form.querySelector('input[name="add-to-cart"]')?.value;
			const quantity = parseInt(formData.get('quantity')) || 1;
			const variationId = formData.get('variation_id') || 0;


			const submitBtn = form.querySelector('[type="submit"]');
			const originalText = submitBtn?.textContent || '';
			const { state } = store('caddy/cart');

			if (submitBtn) {
				submitBtn.textContent = state.i18n.adding;
				submitBtn.disabled = true;
			}

			// Use WooCommerce's native cart add method
			try {
				// Add to WooCommerce cart using standard method
				const addData = new URLSearchParams();
				addData.append('add-to-cart', productId);

				// For grouped products, pass all form data including quantity[id] fields
				for (const [key, value] of formData.entries()) {
					if (key.startsWith('quantity[') || key.startsWith('attribute_') || key === 'variation_id') {
						addData.append(key, value);
					}
				}

				// For simple/variable products, add standard quantity
				if (!form.querySelector('input[name^="quantity["]')) {
					addData.append('quantity', quantity);
					if (variationId) {
						addData.append('variation_id', variationId);
					}
				}


				const response = await fetch(window.location.href, {
					method: 'POST',
					credentials: 'same-origin',
					headers: {
						'Content-Type': 'application/x-www-form-urlencoded',
					},
					body: addData
				});

				if (response.ok) {
					// Refresh cart from WooCommerce Store API
					await refreshCartFromServer();

					// Open cart
					store('caddy/cart').actions.openCart();

					if (submitBtn) {
						const { state } = store('caddy/cart');
						submitBtn.textContent = state.i18n.added;
						setTimeout(() => { if (submitBtn) submitBtn.textContent = originalText; }, 1500);
					}
				} else {
					const { state } = store('caddy/cart');
					throw new Error(state.i18n.errorAddToCartFailed);
				}
			} catch (error) {
				if (submitBtn) {
					const { state } = store('caddy/cart');
					submitBtn.textContent = state.i18n.error;
					setTimeout(() => { if (submitBtn) submitBtn.textContent = originalText; }, 2000);
				}
			} finally {
				if (submitBtn) {
					submitBtn.disabled = false;
				}
			}
		});
	});

	// Handle shop page add-to-cart buttons
	const shopButtons = document.querySelectorAll('a.add_to_cart_button, .wc-block-grid__product-add-to-cart a, .woocommerce ul.products li.product .button[data-product_id]');

	shopButtons.forEach(button => {
		button.addEventListener('click', async function(e) {
			// Skip if already handled by the Interactivity API init callback
			if (this.dataset.caddyIntercepted === 'true') {
				return;
			}

			// Skip variable and grouped product buttons - let them navigate to product page
			if (this.classList.contains('product_type_variable') ||
			    this.classList.contains('product_type_grouped')) {
				return; // Don't prevent default, allow normal navigation
			}

			// Skip "Read more" buttons (non-purchasable products)
			if (!this.classList.contains('add_to_cart_button')) {
				return; // Don't prevent default, allow normal navigation
			}

			e.preventDefault();
			e.stopPropagation();

			const productId = this.dataset.product_id || this.getAttribute('data-product_id');
			const quantity = parseInt(this.dataset.quantity) || 1;

			if (!productId) {
				return;
			}


			// Use CSS classes only to preserve theme icons/markup
			this.disabled = true;
			this.classList.add('loading');

			try {
				// Use WooCommerce Store API as per OPTIMIZATION_PLAN.md requirements

				const addToCartData = {
					id: parseInt(productId),
					quantity: parseInt(quantity)
				};

				const storeApiUrl = `${window.location.origin}/wp-json/wc/store/v1/cart/add-item`;

				// Get Store API nonce
				let storeApiNonce = null;
				const storeApiNonceMeta = document.querySelector('meta[name="wc-store-api-nonce"]');
				if (storeApiNonceMeta) {
					storeApiNonce = storeApiNonceMeta.getAttribute('content');
				}

				const headers = {
					'Content-Type': 'application/json',
				};

				if (storeApiNonce) {
					headers['Nonce'] = storeApiNonce;
				}

				const response = await fetch(storeApiUrl, {
					method: 'POST',
					credentials: 'same-origin',
					headers: headers,
					body: JSON.stringify(addToCartData)
				});


				if (response.ok) {
					const responseData = await response.json();

					// Refresh cart state using Store API
					const cartApiUrl = `${window.location.origin}/wp-json/wc/store/v1/cart`;
					const cartResponse = await fetch(cartApiUrl, {
						credentials: 'same-origin',
						headers: storeApiNonce ? { 'Nonce': storeApiNonce } : {}
					});

					if (cartResponse.ok) {
						const cartData = await cartResponse.json();
						const cartStore = store('caddy/cart');

						// Convert Store API format to Caddy format
						const caddyItems = convertCartItems(cartData.items);

						cartStore.state.items = caddyItems;
						cartStore.state.cartCount = cartData.items_count;
						updateCartTotals(cartStore.state, cartData);
						cartStore.state.coupons = cartData.coupons || [];
						cartStore.state.discountTotal = getStoreApiDiscountTotal(cartStore.state, cartData);

						// Update applied coupons display to refresh discount amount
						updateAppliedCouponsDisplay(cartData.coupons || []);

						// Dispatch cart update event for premium features (rewards meter, etc.)
						document.dispatchEvent(new CustomEvent('caddy_cart_updated', { detail: { cartData } }));
					}

					// Auto-open cart after successful add
					store('caddy/cart').actions.openCart();

					// Show success via CSS class — preserves theme icons/markup
					this.classList.remove('loading');
					this.classList.add('added');
					setTimeout(() => { this.classList.remove('added'); }, 1500);
				} else {
					const errorResponse = await response.text();
					throw new Error(`Store API returned status: ${response.status} - ${errorResponse}`);
				}
			} catch (error) {
				// Silent fail — button returns to normal state
			} finally {
				this.disabled = false;
				this.classList.remove('loading');
			}
		});
	});
});

/**
 * Update item quantity on server
 *
 * @param {string} cartKey Cart item key
 * @param {number} quantity New quantity
 * @returns {Promise}
 */
async function updateQuantityOnServer(cartKey, quantity, itemIndex) {
	// Use WooCommerce Store API for cart item updates

	const storeApiUrl = `${window.location.origin}/wp-json/wc/store/v1/cart/items/${cartKey}`;

	// Get Store API nonce
	let storeApiNonce = null;
	const storeApiNonceMeta = document.querySelector('meta[name="wc-store-api-nonce"]');
	if (storeApiNonceMeta) {
		storeApiNonce = storeApiNonceMeta.getAttribute('content');
	}

	const headers = {
		'Content-Type': 'application/json',
	};

	if (storeApiNonce) {
		headers['Nonce'] = storeApiNonce;
	}

	const requestData = {
		quantity: parseInt(quantity)
	};


	const response = await fetch(storeApiUrl, {
		method: 'PUT',
		headers: headers,
		credentials: 'same-origin',
		body: JSON.stringify(requestData)
	});

	if (!response.ok) {
		const errorText = await response.text();
		const { state } = store('caddy/cart');
		throw new Error(state.i18n.errorUpdateFailed);
	}

	// PUT returns the updated item with correct quantity and pricing
	const updatedItem = await response.json();

	// Update state - just update the specific fields, no need to replace entire item!
	const cartStore = store('caddy/cart');

	// Find the item in state
	const updatedItemIndex = cartStore.state.items.findIndex(item => item.cartKey === updatedItem.key);
	if (updatedItemIndex !== -1) {
		const stateItem = cartStore.state.items[updatedItemIndex];

		// Only update the fields that changed: quantity and pricing
		stateItem.quantity = updatedItem.quantity;

		// Use line_subtotal (undiscounted) for item display, not line_total (discounted)
		// Cart-level discounts should only affect cart totals, not individual item prices
		// Store API totals are always ex-tax; include tax when cart display is tax-inclusive
		let itemTotal = convertStoreApiPrice(updatedItem.totals.line_subtotal);
		if (cartStore.state.taxDisplayCart === 'incl') {
			itemTotal += convertStoreApiPrice(updatedItem.totals.line_subtotal_tax || 0);
		}
		stateItem.lineTotal = itemTotal;

		// Delete and re-assign price to force reactivity
		delete stateItem.price;
		stateItem.price = formatPriceSmart(itemTotal);
		stateItem.priceHtml = formatPrice(itemTotal);

		// Recalculate cart count from all items
		cartStore.state.cartCount = cartStore.state.items.reduce((total, item) => total + item.quantity, 0);
		cartStore.state.isItemSingular = cartStore.state.cartCount === 1;

		// Update empty class and widget
		updateCartEmptyClass(cartStore.state.cartCount);
		updateCartWidgetCount(cartStore.state.cartCount);
	}

	// Fetch full cart to update totals and check for cart structure changes (like free gifts)
	const cartApiUrl = `${window.location.origin}/wp-json/wc/store/v1/cart`;
	const cartResponse = await fetch(cartApiUrl, {
		credentials: 'same-origin',
		headers: storeApiNonce ? { 'Nonce': storeApiNonce } : {}
	});

	if (cartResponse.ok) {
		const cartData = await cartResponse.json();

		// Check if items were added/removed (e.g., free gifts from reward tiers)
		// Compare number of distinct items, not total quantity
		// Also filter server items by pendingRemovals to avoid re-adding items being removed
		const serverItemsFiltered = pendingRemovals.size > 0
			? cartData.items.filter(item => !pendingRemovals.has(item.key))
			: cartData.items;

		if (serverItemsFiltered.length !== cartStore.state.items.length) {
			// Cart structure changed - add/remove items without replacing entire array
			const serverKeys = new Set(serverItemsFiltered.map(item => item.key));
			const stateKeys = new Set(cartStore.state.items.map(item => item.cartKey));

			// Find items to add (in server but not in state)
			const itemsToAdd = serverItemsFiltered.filter(item => !stateKeys.has(item.key));

			// Find items to remove (in state but not in server)
			const keysToRemove = [...stateKeys].filter(key => !serverKeys.has(key));

			// Remove items that are no longer in cart
			if (keysToRemove.length > 0) {
				cartStore.state.items = cartStore.state.items.filter(item => !keysToRemove.includes(item.cartKey));
			}

			// Add new items (like free gifts)
			if (itemsToAdd.length > 0) {
				const newCaddyItems = itemsToAdd.map(item => convertStoreApiItemToCaddyFormat(item));
				cartStore.state.items = [...cartStore.state.items, ...newCaddyItems];
			}

			// Update counts
			cartStore.state.cartCount = cartStore.state.items.reduce((total, item) => total + item.quantity, 0);
			cartStore.state.isItemSingular = cartStore.state.cartCount === 1;
			updateCartEmptyClass(cartStore.state.cartCount);
			updateCartWidgetCount(cartStore.state.cartCount);
		}

		// Always update totals (handles coupons and discounts)
		updateCartTotals(cartStore.state, cartData);
		cartStore.state.coupons = cartData.coupons || [];
		cartStore.state.discountTotal = getStoreApiDiscountTotal(cartStore.state, cartData);

		// Update applied coupons display
		updateAppliedCouponsDisplay(cartData.coupons || []);

		// Dispatch cart update event for premium features (rewards meter, etc.)
		document.dispatchEvent(new CustomEvent('caddy_cart_updated', { detail: { cartData } }));
	}

	// Broadcast update to other tabs
	broadcastCartUpdate();

	return { success: true };
}

/**
 * Remove item from server
 *
 * @param {string} cartKey Cart item key
 * @returns {Promise}
 */
/**
 * WCS-ATT: Hydrate subscription scheme data for cart items that don't have it yet.
 * Called after client-side add-to-cart since Store API doesn't include WCS-ATT data.
 */
async function hydrateSubscriptionSchemes(state) {
	// Skip if WCS-ATT isn't active
	if (!state.wcsAttAjaxUrl) return;

	// Only hydrate items that haven't been checked yet (undefined = from Store API converter)
	const needsHydration = state.items.some(item => typeof item.hasSubscriptionSchemes === 'undefined');
	if (!needsHydration) return;

	try {
		const restUrl = document.querySelector('meta[name="caddy-rest-url"]')?.content;
		if (!restUrl) return;

		const nonce = document.querySelector('meta[name="caddy-nonce"]')?.content;
		const response = await fetch(`${restUrl}cart-subscription-schemes`, {
			credentials: 'same-origin',
			headers: nonce ? { 'X-WP-Nonce': nonce } : {}
		});

		if (!response.ok) return;
		const schemesData = await response.json();

		// Merge subscription data into matching cart items
		for (const item of state.items) {
			if (typeof item.hasSubscriptionSchemes !== 'undefined') continue; // Already hydrated
			const data = schemesData[item.cartKey];
			if (data) {
				item.hasSubscriptionSchemes = true;
				item.showUpgradeButton = data.showUpgradeButton;
				item.showSubscriptionDropdown = data.showSubscriptionDropdown;
				item.upgradeButtonText = data.upgradeButtonText;
				item.subscriptionOptions = data.subscriptionOptions;
			} else {
				item.hasSubscriptionSchemes = false; // Mark as hydrated (no schemes)
			}
		}
	} catch (error) {
		// Non-critical, fail silently
	}
}

/**
 * WCS-ATT: Send AJAX to update subscription scheme for a cart item
 */
async function updateSubscriptionScheme(item, schemeKey, state, switchingToOneTime = false) {
	const ajaxUrl = state.wcsAttAjaxUrl;
	const nonce = state.wcsAttNonce;
	const cartKey = item.cartKey;

	if (!ajaxUrl) return;

	item.subscriptionUpdating = true;
	item.subscriptionConfirmed = false;

	try {
		const formData = new FormData();
		formData.append('scheme_key', schemeKey);
		formData.append('cart_item_key', cartKey);
		formData.append('security', nonce);

		const response = await fetch(ajaxUrl, {
			method: 'POST',
			body: formData,
			credentials: 'same-origin'
		});

		const data = await response.json();

		if (!data.success) {
			throw new Error(data.data || 'Subscription update failed');
		}

		// Refresh cart from server to pick up updated prices
		await refreshCartFromServer();

		// Find the (possibly new) item reference after refresh
		const current = state.items.find(i => i.cartKey === cartKey);
		if (current) {
			current.subscriptionUpdating = false;
			current.subscriptionConfirmed = true;

			// Toggle visibility after AJAX completes (so spinner shows during request)
			if (switchingToOneTime) {
				current.showUpgradeButton = true;
				current.showSubscriptionDropdown = false;
			}

			setTimeout(() => {
				current.subscriptionConfirmed = false;
			}, 2000);
		}
	} catch (error) {
		console.error('Failed to update subscription scheme:', error);
		const current = state.items.find(i => i.cartKey === cartKey);
		if (current) {
			current.subscriptionUpdating = false;
		}
	}
}

async function removeItemFromServer(cartKey) {
	// Use WooCommerce Store API POST remove-item endpoint (returns full cart data,
	// unlike DELETE /cart/items/{key} which returns 204 No Content and prevents
	// the JS from learning about server-side changes like free gift/coupon removal)
	const storeApiUrl = `${window.location.origin}/wp-json/wc/store/v1/cart/remove-item`;

	// Get Store API nonce
	let storeApiNonce = null;
	const storeApiNonceMeta = document.querySelector('meta[name="wc-store-api-nonce"]');
	if (storeApiNonceMeta) {
		storeApiNonce = storeApiNonceMeta.getAttribute('content');
	}

	const headers = {
		'Content-Type': 'application/json',
	};

	if (storeApiNonce) {
		headers['Nonce'] = storeApiNonce;
	}


	const response = await fetch(storeApiUrl, {
		method: 'POST',
		headers: headers,
		credentials: 'same-origin',
		keepalive: true,
		body: JSON.stringify({ key: cartKey })
	});

	if (!response.ok) {
		let errorData;
		try {
			errorData = await response.json();
		} catch (e) {
			errorData = await response.text();
		}

		console.error('Failed to remove cart item:', {
			status: response.status,
			statusText: response.statusText,
			cartKey: cartKey,
			url: storeApiUrl,
			error: errorData,
			currentCartState: store('caddy/cart').state.items.map(i => ({ key: i.cartKey, name: i.name }))
		});

		const { state } = store('caddy/cart');
		throw new Error(state.i18n.errorRemoveFailed);
	}

	// Check if response has content before parsing
	const contentType = response.headers.get('content-type');
	let cartData = null;

	// Only try to parse JSON if there's content
	if (contentType && contentType.includes('application/json')) {
		const text = await response.text();
		if (text && text.trim().length > 0) {
			try {
				cartData = JSON.parse(text);
			} catch (e) {
				console.warn('Remove-item response is not valid JSON, skipping cart data update:', text);
			}
		}
	}

	if (cartData) {

		// Update state with fresh cart data (this ensures free gifts are removed when threshold not met)
		const cartStore = store('caddy/cart');

		// Convert Store API format to Caddy format
		const serverItems = convertCartItems(cartData.items);

		// Filter out items that are still being removed by concurrent requests
		// (prevents rapid-removal race condition where server response re-adds items)
		const filteredItems = pendingRemovals.size > 0
			? serverItems.filter(item => !pendingRemovals.has(item.cartKey))
			: serverItems;

		// Don't replace the entire items array — the optimistic removal already
		// removed the item from state. Only make surgical changes if the server
		// response shows unexpected differences (e.g., free gift auto-removed,
		// bundle children removed, coupon threshold changes).
		const currentKeys = new Set(cartStore.state.items.map(item => item.cartKey));
		const serverKeys = new Set(filteredItems.map(item => item.cartKey));

		// Remove items that server no longer has (beyond the optimistic removal)
		for (let i = cartStore.state.items.length - 1; i >= 0; i--) {
			if (!serverKeys.has(cartStore.state.items[i].cartKey)) {
				cartStore.state.items.splice(i, 1);
			}
		}

		// Never re-add items during a remove response — stale server sessions
		// can return already-removed items. Only refreshCartFromServer() adds items.

		cartStore.state.cartCount = cartStore.state.items.reduce((total, item) => total + item.quantity, 0);
		updateCartEmptyClass(cartStore.state.cartCount);
		updateCartWidgetCount(cartStore.state.cartCount);

		// Recalculate totals from LOCAL items (not server response which may include
		// stale items still pending removal on server side)
		updateCartTotalsFromItems(cartStore.state);

		cartStore.state.cartHash = cartData.cartHash || 'updated';
		cartStore.state.coupons = cartData.coupons || [];
		cartStore.state.discountTotal = getStoreApiDiscountTotal(cartStore.state, cartData);

		// Update widget counts
		updateCartWidgetCount(cartStore.state.cartCount);

		// Update applied coupons display to refresh discount amount
		updateAppliedCouponsDisplay(cartData.coupons || []);

		// Dispatch cart update event for premium features (rewards meter, etc.)
		document.dispatchEvent(new CustomEvent('caddy_cart_updated', { detail: { cartData } }));

		// Refresh recommendations based on remaining cart items
		refreshRecommendationsFromServer(cartStore.state);
	} else {
		// No cart data in response — fall back to full server refresh
		// to pick up server-side changes (free gift removal, coupon cleanup, etc.)
		await refreshCartFromServer();
	}

	// Broadcast update to other tabs
	broadcastCartUpdate();

	return { success: true };
}

/**
 * Calculate original total (before sale discounts) from cart items
 *
 * @param {Array} items Cart items array
 * @returns {number} Original total in dollars
 */
function calculateOriginalTotal(items) {
	if (!items || items.length === 0) return 0;
	return items.reduce((total, item) => {
		const regularPrice = item.regularPrice || 0;
		const quantity = item.quantity || 0;
		return total + (regularPrice * quantity);
	}, 0);
}

function getStoreApiDiscountTotal(state, cartData) {
	let couponDiscount = convertStoreApiPrice(cartData?.totals?.total_discount || 0);
	if (state?.taxDisplayCart === 'incl') {
		couponDiscount += convertStoreApiPrice(cartData?.totals?.total_discount_tax || 0);
	}
	return couponDiscount;
}

function getStoreApiSubtotal(state, cartData) {
	let itemsTotal = convertStoreApiPrice(cartData?.totals?.total_items || 0);
	if (state?.taxDisplayCart === 'incl') {
		itemsTotal += convertStoreApiPrice(cartData?.totals?.total_items_tax || 0);
	}
	return itemsTotal - getStoreApiDiscountTotal(state, cartData);
}

/**
 * Update all cart totals including subtotal, original total, and discount flag
 * Call this function whenever the cart is updated to ensure all prices are synchronized
 *
 * @param {Object} state Cart state object
 * @param {Object} cartData WooCommerce Store API cart data
 */
function updateCartTotals(state, cartData) {
	// Store API totals are ex-tax; include tax totals when cart display is inclusive.
	state.cartSubtotal = getStoreApiSubtotal(state, cartData);
	state.cartSubtotalDisplay = formatPriceSmart(state.cartSubtotal);
	state.cartSubtotalFormatted = formatPrice(state.cartSubtotal);

	// Calculate original total (before sale discounts)
	state.originalTotal = calculateOriginalTotal(state.items);
	state.originalTotalDisplay = formatPriceSmart(state.originalTotal);
	state.originalTotalFormatted = formatPrice(state.originalTotal);

	// Set discount flag (only show crossed-out price if there's an actual discount)
	state.hasDiscount = state.originalTotal > state.cartSubtotal;

	// Update grand total
	state.cartTotal = cartData.totals.total_formatted || formatPrice(convertStoreApiPrice(cartData.totals.total_price));
}

/**
 * Update cart totals after optimistic client-side changes (rollbacks, etc)
 * Use this when you don't have server data but need to recalculate from items
 *
 * @param {Object} state Cart state object
 * @param {boolean} skipSubtotal If true, skips subtotal recalculation (use when subtotal already set)
 */
function updateCartTotalsFromItems(state, skipSubtotal = false) {
	// Calculate subtotal from items (unless already calculated)
	if (!skipSubtotal) {
		state.cartSubtotal = state.items.reduce((total, item) => total + item.lineTotal, 0);
		state.cartSubtotalDisplay = formatPriceSmart(state.cartSubtotal);
		state.cartSubtotalFormatted = formatPrice(state.cartSubtotal);
		state.cartTotal = formatPrice(state.cartSubtotal);
	} else {
		// Even when skipping subtotal calc, update formatted values to match current subtotal
		state.cartSubtotalDisplay = formatPriceSmart(state.cartSubtotal);
		state.cartSubtotalFormatted = formatPrice(state.cartSubtotal);
		state.cartTotal = formatPrice(state.cartSubtotal);
	}

	// Calculate original total (before sale discounts)
	state.originalTotal = calculateOriginalTotal(state.items);
	state.originalTotalDisplay = formatPriceSmart(state.originalTotal);
	state.originalTotalFormatted = formatPrice(state.originalTotal);

	// Set discount flag (only show crossed-out price if there's an actual discount)
	state.hasDiscount = state.originalTotal > state.cartSubtotal;
}

/**
 * Set up cross-tab synchronization
 */
function setupCrossTabSync() {

	// Option 1: Use BroadcastChannel if available (best performance)
	if ('BroadcastChannel' in window) {
		const channel = new BroadcastChannel('caddy_cart_sync');

		// Listen for updates from other tabs
		channel.addEventListener('message', async (event) => {

			if (event.data.type === 'cart_updated') {
				// Fetch fresh cart state from server
				await refreshCartFromServer();
			}
		});

		// Store reference to channel for broadcasting
		window.caddyCartChannel = channel;
	}
	// Option 2: Fallback to localStorage events
	else {
		window.addEventListener('storage', async (event) => {
			if (event.key === 'caddy_cart_update') {
				// Fetch fresh cart state from server
				await refreshCartFromServer();
			}
		});
	}

	// Also poll for updates periodically when tab becomes visible
	document.addEventListener('visibilitychange', async () => {
		if (!document.hidden) {
			await refreshCartFromServer();
		}
	});

	// Flush any pending removals before page unload so they aren't lost.
	// Fires all at once with keepalive — 409s don't matter since we're leaving.
	window.addEventListener('beforeunload', () => {
		if (pendingRemovals.size === 0) return;
		const storeApiUrl = `${window.location.origin}/wp-json/wc/store/v1/cart/remove-item`;
		const storeApiNonceMeta = document.querySelector('meta[name="wc-store-api-nonce"]');
		const nonce = storeApiNonceMeta?.getAttribute('content');
		const headers = { 'Content-Type': 'application/json' };
		if (nonce) headers['Nonce'] = nonce;

		for (const cartKey of pendingRemovals) {
			fetch(storeApiUrl, {
				method: 'POST',
				headers,
				credentials: 'same-origin',
				keepalive: true,
				body: JSON.stringify({ key: cartKey })
			});
		}
	});
}

/**
 * Broadcast cart update to other tabs
 */
function broadcastCartUpdate() {

	// Use BroadcastChannel if available
	if (window.caddyCartChannel) {
		window.caddyCartChannel.postMessage({
			type: 'cart_updated',
			timestamp: Date.now()
		});
	}
	// Fallback to localStorage
	else {
		localStorage.setItem('caddy_cart_update', JSON.stringify({
			timestamp: Date.now()
		}));
		// Clean up after a moment
		setTimeout(() => {
			localStorage.removeItem('caddy_cart_update');
		}, 100);
	}
}

// Flag to prevent concurrent refreshes
let isRefreshing = false;
// Flag to queue a refresh if one was requested while another was in-flight
let refreshQueued = false;

// Track cart keys being removed to prevent refresh from re-adding them
const pendingRemovals = new Set();

// Serialize server-side cart mutations to prevent 409 Conflict errors.
// WooCommerce Store API locks the cart during writes — concurrent POSTs get rejected.
// Optimistic UI updates are still instant; only the server calls are queued.
let cartMutationChain = Promise.resolve();

function queueCartMutation(fn) {
	const result = cartMutationChain.then(fn, fn);
	cartMutationChain = result.catch(() => {}); // prevent unhandled rejection from breaking chain
	return result;
}

/**
 * Update the cart empty class based on cart count
 * @param {number} cartCount - Current number of items in cart
 */
function updateCartEmptyClass(cartCount) {
	const cartBody = document.querySelector('.cc-cart .cc-body');
	if (!cartBody) return;

	if (cartCount === 0) {
		cartBody.classList.add('cc-empty');
	} else {
		cartBody.classList.remove('cc-empty');
	}
}

/**
 * Update cart widget count
 *
 * @param {number} count Cart items count
 */
function updateCartWidgetCount(count) {
	const cartCountElements = document.querySelectorAll('.cc_cart_count');
	cartCountElements.forEach(element => {
		element.textContent = count;

		// Update classes for zero state
		if (count === 0) {
			element.classList.add('cc_cart_zero');
		} else {
			element.classList.remove('cc_cart_zero');
		}
	});
}

/**
 * Update the saved items empty class based on saved items count
 * @param {number} savedItemsCount - Current number of saved items
 */
function updateSavedItemsEmptyClass(savedItemsCount) {
	const savedBody = document.querySelector('.cc-saves .cc-body');
	const emptyState = document.getElementById('cc-empty-saved-items');

	if (!savedBody) return;

	if (savedItemsCount === 0) {
		savedBody.classList.add('cc-empty');
		// Show the empty state message
		if (emptyState) {
			emptyState.classList.remove('cc-hidden');
		}
	} else {
		savedBody.classList.remove('cc-empty');
		// Hide the empty state message
		if (emptyState) {
			emptyState.classList.add('cc-hidden');
		}
	}
}

/**
 * Refresh cart state from server
 */
async function refreshCartFromServer() {
	// Prevent concurrent refreshes — queue a retry instead of dropping
	if (isRefreshing) {
		refreshQueued = true;
		return;
	}

	isRefreshing = true;

	// Disable cart during sync to prevent interaction glitches
	const { state } = store('caddy/cart');
	state.isLoading = true;

	try {
		// Use Store API instead of custom Caddy endpoint (which returns empty state)
		const storeApiNonce = document.querySelector('meta[name="wc-store-api-nonce"]')?.getAttribute('content');
		const response = await fetch('/wp-json/wc/store/v1/cart', {
			credentials: 'same-origin',
			headers: storeApiNonce ? { 'Nonce': storeApiNonce } : {}
		});

		// Refresh nonce from response headers for long-lived tabs
		refreshNonceFromResponse(response);

		const cartData = await response.json();

		if (response.ok && cartData) {

			// Convert Store API format to Caddy format
			if (cartData.items && cartData.items.length > 0) {
				const caddyItems = cartData.items.map(item => {
					const newItem = convertStoreApiItemToCaddyFormat(item);
					// Preserve flags from existing items during refresh
					const existing = state.items.find(old => old.cartKey === newItem.cartKey);
					if (existing) {
						if (existing.isUpdating) {
							newItem.isUpdating = true;
						}
						// Preserve WCS-ATT subscription state (not available from Store API)
						if (existing.hasSubscriptionSchemes) {
							newItem.hasSubscriptionSchemes = existing.hasSubscriptionSchemes;
							newItem.showUpgradeButton = existing.showUpgradeButton;
							newItem.showSubscriptionDropdown = existing.showSubscriptionDropdown;
							newItem.upgradeButtonText = existing.upgradeButtonText;
							newItem.subscriptionOptions = existing.subscriptionOptions;
							newItem.subscriptionUpdating = existing.subscriptionUpdating;
							newItem.subscriptionConfirmed = existing.subscriptionConfirmed;
						}
					}
					return newItem;
				});

				// Filter out items that are still being removed by concurrent requests
				const filteredItems = pendingRemovals.size > 0
					? caddyItems.filter(item => !pendingRemovals.has(item.cartKey))
					: caddyItems;

				// Always update after coupon operations or explicit refresh calls
				state.items = filteredItems;
				state.cartCount = filteredItems.reduce((total, item) => total + item.quantity, 0);
				state.isItemSingular = cartData.items_count === 1;
				updateCartTotals(state, cartData);
				state.coupons = cartData.coupons || [];
				state.discountTotal = getStoreApiDiscountTotal(state, cartData);
				updateCartEmptyClass(state.cartCount);
				updateCartWidgetCount(state.cartCount);

				// Update applied coupons display to refresh discount amount
				updateAppliedCouponsDisplay(cartData.coupons || []);

				// Dispatch cart update event for premium features (rewards meter, etc.)
				document.dispatchEvent(new CustomEvent('caddy_cart_updated', { detail: { cartData } }));
			} else {
				// Cart is empty on server
				state.items = [];
				state.cartCount = 0;
				updateCartEmptyClass(state.cartCount);
				updateCartWidgetCount(state.cartCount);
				state.cartSubtotal = 0;
				state.cartSubtotalDisplay = formatPriceSmart(0);
				state.cartSubtotalFormatted = formatPrice(0);
				state.cartTotal = formatPrice(0);
				state.coupons = [];
				state.discountTotal = 0;

				// Update widget counts
				updateCartWidgetCount(state.cartCount);

				// Dispatch cart update event for premium features (rewards meter, etc.)
				// Create a minimal cartData object for empty cart
				const emptyCartData = {
					items: [],
					items_count: 0,
					totals: {
						total_items: '0',
						total_price: '0',
						total_discount: '0'
					},
					coupons: []
				};
				document.dispatchEvent(new CustomEvent('caddy_cart_updated', { detail: { cartData: emptyCartData } }));
			}
		}
	} catch (error) {
		console.warn('Caddy: Failed to refresh cart from server:', error);
	} finally {
		isRefreshing = false;
		// Re-enable cart after sync
		state.isLoading = false;

		// If another refresh was requested while we were busy, run it now
		if (refreshQueued) {
			refreshQueued = false;
			refreshCartFromServer();
		}
	}
}

/**
 * Initialize recommendations functionality
 * This is a wrapper that calls the module version if loaded, otherwise does nothing
 */
function initializeRecommendations() {
	if (window._caddyInitializeRecommendations) {
		window._caddyInitializeRecommendations();
		return;
	}
	// Stub - recommendations module not loaded yet
}

// Track if prefetch has been scheduled
let recommendationsPrefetchScheduled = false;

/**
 * Schedule background prefetch for recommendations using requestIdleCallback
 * This allows the main page to load first, then fetch recommendations during idle time
 */
function scheduleRecommendationsPrefetch() {
	if (recommendationsPrefetchScheduled) return;
	recommendationsPrefetchScheduled = true;

	const { state } = store('caddy/cart');

	// If recommendations aren't available in state, skip
	if (state.recommendations === undefined) return;

	// If already have recommendations, just mark loading complete
	if (state.recommendations.length > 0) {
		state.recommendationsLoading = false;
		return;
	}

	const prefetch = () => {
		refreshRecommendationsFromServer(state);
	};

	// Use requestIdleCallback for background prefetch (Safari fallback to setTimeout)
	if ('requestIdleCallback' in window) {
		requestIdleCallback(prefetch, { timeout: 2000 });
	} else {
		setTimeout(prefetch, 100);
	}
}

/**
 * Refresh recommendations from server API and update state
 * @param {Object} state - The cart state object
 */
async function refreshRecommendationsFromServer(state) {
	try {
		// Get the last product in cart for recommendations
		// Skip bundled child items — use the last top-level or bundle container item
		let productId = 0;
		if (state.items && state.items.length > 0) {
			const topLevelItems = state.items.filter(item => !item.isBundledItem);
			const lastItem = topLevelItems.length > 0
				? topLevelItems[topLevelItems.length - 1]
				: state.items[state.items.length - 1];
			productId = lastItem.productId || 0;
		}

		// Get cart product IDs to exclude
		const cartProductIds = state.items ? state.items.map(item => item.productId) : [];
		const excludeParam = cartProductIds.length > 0 ? `?exclude=${cartProductIds.join(',')}` : '';

		// Fetch from Premium recommendations endpoint (respects workflows)
		const response = await fetch(`/wp-json/caddy/v1/recommendations/${productId}${excludeParam}`, {
			credentials: 'same-origin',
			headers: { 'Content-Type': 'application/json' }
		});

		if (!response.ok) {
			throw new Error('Failed to fetch recommendations');
		}

		const data = await response.json();
		const products = data.products || [];

		// Format products for Interactivity API state
		const formattedProducts = products.map(product => {
			const regularPrice = product.prices?.regular_price ? convertStoreApiPrice(product.prices.regular_price) : 0;
			const salePrice = product.prices?.sale_price ? convertStoreApiPrice(product.prices.sale_price) : null;
			const isOnSale = salePrice && salePrice < regularPrice;
			let currentPrice = isOnSale ? salePrice : regularPrice;

			const isVariable = product.type === 'variable';

			// For variable products, filter to in-stock variations and calculate min price
			let variations = [];
			let pricePrefix = '';
			if (isVariable && product.variations?.length) {
				// Filter to only in-stock, purchasable variations
				variations = product.variations.filter(v => v.is_in_stock && v.is_purchasable);

				// Calculate min price from available variations
				if (variations.length > 0) {
					const minPrice = Math.min(...variations.map(v => v.display_price || 0));
					currentPrice = minPrice;
					const fromLabel = state.i18n?.priceFrom || 'From';
					pricePrefix = `${fromLabel} `;
				}
			}

				const decodedProductName = decodeHTMLEntitiesDeep(product.name || '');

				return {
					id: product.id,
					name: decodedProductName,
					permalink: product.permalink,
				image: (product.images?.[0]?.src || getWooCommercePlaceholderImage()).replace(/([^:])\/\/+/g, '$1/'),
				price: `${pricePrefix}${formatPrice(currentPrice)}`,
				regularPrice: formatPrice(regularPrice),
				isOnSale: isOnSale && !isVariable, // Don't show sale styling for variable products
				isVariable: isVariable,
				isGrouped: product.type === 'grouped',
				isSimple: product.type !== 'variable' && product.type !== 'grouped',
				buttonText: product.type === 'variable' ? (state.i18n?.addToCart || 'Add to cart') :
				            product.type === 'grouped' ? (state.i18n?.viewProducts || 'View products') : (state.i18n?.addToCart || 'Add to cart'),
				isAdding: false,
				// Variable product data - only in-stock variations
				variations: variations,
				attributes: isVariable ? (product.attributes || []) : [],
				selectedVariationId: 0,
				variationSelected: false
			};
		});

		// Update state
		state.recommendations = formattedProducts;
		state.recommendationsLoading = false;
		state.showRecommendations = formattedProducts.length > 0;
		state.recommendationIndex = 0; // Reset to first slide

		// Build variation selects for variable products after DOM updates
		setTimeout(() => buildVariationSelects(state), 100);

	} catch (error) {
		// On error, hide recommendations
		state.recommendations = [];
		state.recommendationsLoading = false;
		state.showRecommendations = false;
	}
}

/**
 * Build variation select dropdowns for variable products
 * Called after recommendations are loaded and DOM is updated
 * @param {Object} state - The cart state object
 */
function buildVariationSelects(state) {
	const containers = document.querySelectorAll('.cc-variations-container');

	containers.forEach(container => {
		const productId = parseInt(container.dataset.productId);
		if (!productId) return;

		// Find the product in recommendations
		const product = state.recommendations?.find(rec => rec.id === productId);
		if (!product || !product.isVariable || !product.attributes?.length) return;

		// Skip if no in-stock variations
		if (!product.variations?.length) return;

		// Clear existing content
		container.innerHTML = '';

		// Build select for each attribute
		product.attributes.forEach(attr => {
			const selectTemplate = state.i18n?.selectAttribute || 'Select %s';
			const selectText = selectTemplate.includes('%s')
				? selectTemplate.replace('%s', attr.name)
				: `${selectTemplate} ${attr.name}`;

			// Filter options to only those that have at least one IN-STOCK, PURCHASABLE variation
			const availableOptions = attr.options.filter(optionValue => {
				return product.variations.some(variation => {
					// Must be in stock and purchasable
					if (!variation.is_in_stock || !variation.is_purchasable) return false;

					// Find the variation attribute value (case-insensitive key lookup)
					// WooCommerce may use "attribute_color" or "attribute_Color" inconsistently
					const attrKeyLower = `attribute_${attr.slug}`.toLowerCase();
					const variationValue = Object.entries(variation.attributes).find(
						([key]) => key.toLowerCase() === attrKeyLower
					)?.[1];

					// Only match if variation specifically has this value
					// (empty variationValue means "Any" - we still need exact match for filtering)
					if (!variationValue) return true; // "Any" variations match all options
					return variationValue.toLowerCase() === optionValue.toLowerCase();
				});
			});

			// Skip attribute if no available options
			if (!availableOptions.length) return;

			const wrapper = document.createElement('div');
			wrapper.className = 'variation-attribute';

			const select = document.createElement('select');
			select.className = 'variation-select';
			select.dataset.productId = productId;
			select.dataset.attributeSlug = attr.slug;

			// Add default option
			const defaultOption = document.createElement('option');
			defaultOption.value = '';
			defaultOption.textContent = selectText;
			select.appendChild(defaultOption);

			// Add only available attribute options
			availableOptions.forEach(optionValue => {
				const option = document.createElement('option');
				option.value = optionValue;
				option.textContent = optionValue;
				select.appendChild(option);
			});

			// Add change event listener
			select.addEventListener('change', (e) => handleVariationChange(e, state));

			wrapper.appendChild(select);
			container.appendChild(wrapper);
		});
	});
}

/**
 * Handle variation select change
 * @param {Event} event - Change event
 * @param {Object} state - Cart state
 */
function handleVariationChange(event, state) {
	const select = event.target;
	const productId = parseInt(select.dataset.productId);
	const attrSlug = select.dataset.attributeSlug;
	const value = select.value;

	// Find the product in recommendations
	const item = state.recommendations?.find(rec => rec.id === productId);
	if (!item || !item.variations) return;

	// Initialize selectedAttributes if not exists
	if (!item.selectedAttributes) {
		item.selectedAttributes = {};
	}

	// Store the selected value
	if (value) {
		item.selectedAttributes[attrSlug] = value;
	} else {
		delete item.selectedAttributes[attrSlug];
	}

	// Update other dropdowns based on current selection (dependent filtering)
	updateDependentDropdowns(productId, item, state);

	// Check if all attributes are selected
	const allSelected = item.attributes.every(attr =>
		item.selectedAttributes[attr.slug]
	);

	if (allSelected) {
		// Helper: case-insensitive attribute value lookup
		const getVariationAttrValue = (variation, slug) => {
			const keyLower = `attribute_${slug}`.toLowerCase();
			return Object.entries(variation.attributes).find(
				([key]) => key.toLowerCase() === keyLower
			)?.[1];
		};

		// Find matching variation
		const matchingVariation = item.variations.find(variation => {
			return Object.entries(item.selectedAttributes).every(([slug, val]) => {
				const variationValue = getVariationAttrValue(variation, slug);
				// Match if variation allows any value (empty) or matches exactly
				return !variationValue || variationValue.toLowerCase() === val.toLowerCase();
			});
		});

		if (matchingVariation && matchingVariation.is_in_stock && matchingVariation.is_purchasable) {
			item.selectedVariationId = matchingVariation.variation_id;
			item.variationSelected = true;
			// Update price display
			item.price = formatPrice(matchingVariation.display_price);
			item.priceHtml = formatPrice(matchingVariation.display_price);
		} else {
			item.selectedVariationId = 0;
			item.variationSelected = false;
		}
	} else {
		item.selectedVariationId = 0;
		item.variationSelected = false;
	}
}

/**
 * Update dependent dropdowns based on current selections
 * Filters options in other dropdowns to only show valid combinations
 * @param {number} productId - Product ID
 * @param {Object} item - Product item from state
 * @param {Object} state - Cart state
 */
function updateDependentDropdowns(productId, item, state) {
	const container = document.querySelector(`.cc-variations-container[data-product-id="${productId}"]`);
	if (!container) return;

	const selects = container.querySelectorAll('.variation-select');

	selects.forEach(select => {
		const thisAttrSlug = select.dataset.attributeSlug;
		const currentValue = select.value;

		// Find this attribute's full options list
		const attr = item.attributes.find(a => a.slug === thisAttrSlug);
		if (!attr) return;

		// Get currently selected attributes (excluding this one)
		const otherSelections = { ...item.selectedAttributes };
		delete otherSelections[thisAttrSlug];

		// Helper: case-insensitive attribute value lookup
		const getVariationAttrValue = (variation, slug) => {
			const keyLower = `attribute_${slug}`.toLowerCase();
			return Object.entries(variation.attributes).find(
				([key]) => key.toLowerCase() === keyLower
			)?.[1];
		};

		// Filter options to only those that have a valid IN-STOCK variation with current selections
		const availableOptions = attr.options.filter(optionValue => {
			return item.variations.some(variation => {
				// Must be in stock and purchasable
				if (!variation.is_in_stock || !variation.is_purchasable) return false;

				// Check if this variation matches all OTHER selected attributes
				const matchesOtherSelections = Object.entries(otherSelections).every(([slug, val]) => {
					const variationValue = getVariationAttrValue(variation, slug);
					return !variationValue || variationValue.toLowerCase() === val.toLowerCase();
				});

				if (!matchesOtherSelections) return false;

				// Check if this variation matches this option
				const variationValue = getVariationAttrValue(variation, thisAttrSlug);
				return !variationValue || variationValue.toLowerCase() === optionValue.toLowerCase();
			});
		});

		// Update the select options
		const selectTemplate = state.i18n?.selectAttribute || 'Select %s';
		const fallbackSelectText = selectTemplate.includes('%s')
			? selectTemplate.replace('%s', attr.name)
			: `${selectTemplate} ${attr.name}`;
		const defaultOptionText = select.options[0]?.textContent || fallbackSelectText;

		// Remember current selection
		const selectedValue = select.value;

		// Clear and rebuild options
		select.innerHTML = '';

		// Add default option
		const defaultOption = document.createElement('option');
		defaultOption.value = '';
		defaultOption.textContent = defaultOptionText;
		select.appendChild(defaultOption);

		// Add available options
		availableOptions.forEach(optionValue => {
			const option = document.createElement('option');
			option.value = optionValue;
			option.textContent = optionValue;
			if (optionValue === selectedValue) {
				option.selected = true;
			}
			select.appendChild(option);
		});

		// If current selection is no longer available, reset it
		if (selectedValue && !availableOptions.includes(selectedValue)) {
			select.value = '';
			delete item.selectedAttributes[thisAttrSlug];
		}
	});
}


// Initialize cross-tab synchronization and load cart data after all functions are defined
document.addEventListener('DOMContentLoaded', async () => {
	setupCrossTabSync();

	// Initialize coupon functionality
	initializeCouponDrawer();
	initializeCouponForm();

	// Lazy-load Recommendations module if enabled and container exists
	const premiumState = store('caddy/premium');
	const recommendationsExists = document.getElementById('cc-store-api-recommendations');
	if (premiumState?.state?.recommendations?.enabled && recommendationsExists) {
		try {
			const { initializeRecommendationsModule } = await import('./modules/caddy-recommendations.js');
			const recsModule = initializeRecommendationsModule({
				updateCartTotals,
				updateAppliedCouponsDisplay
			});
			// Override global initializeRecommendations with module version
			window._caddyInitializeRecommendations = recsModule.initializeRecommendations;
		} catch (error) {
			console.error('Failed to load Recommendations module:', error);
		}
	}

	// Lazy-load Save for Later module if enabled and SFL elements are present in DOM
	const sflTabExists = document.querySelector('.cc-save-nav, #cc-saves, .save_for_later_btn, .cc_add_product_to_sfl, .remove_from_sfl_button, .cc-heart-icon, .cc-sfl-btn');

	if (premiumState?.state?.saveForLater?.enabled && sflTabExists) {
		try {
			const { initializeSaveForLater } = await import('./modules/caddy-sfl.js');
			initializeSaveForLater({
				updateCartTotalsFromItems,
				removeItemFromServer,
				refreshCartFromServer,
				updateCartEmptyClass,
				updateCartWidgetCount,
				updateCartTotals,
				updateAppliedCouponsDisplay,
				initializeRecommendations
			});
		} catch (error) {
			console.error('Failed to load Save for Later module:', error);
		}
	}

	// Lazy-load Rewards Meter module if enabled and rewards meter elements are present in DOM
	const rewardsMeterExists = document.querySelector('.cc-rewards-meters-container, .tier-marker, .cc-fs-meter');

	if (premiumState?.state?.rewardsMeter?.enabled && rewardsMeterExists) {
		try {
			const { initializeRewardsMeter } = await import('./modules/caddy-rewards-meter.js');
			initializeRewardsMeter({
				updateCartTotalsFromItems,
				removeItemFromServer,
				refreshCartFromServer,
				updateCartEmptyClass,
				updateCartWidgetCount,
				updateCartTotals,
				updateAppliedCouponsDisplay
			});
		} catch (error) {
			console.error('Failed to load Rewards Meter module:', error);
		}
	}

	// Lazy-load Announcement Bar module if enabled
	if (premiumState?.state?.announcement?.enabled) {
		try {
			const { initializeAnnouncementBar } = await import('./modules/caddy-announcement-bar.js');
			initializeAnnouncementBar({
				updateCartTotalsFromItems,
				removeItemFromServer,
				refreshCartFromServer,
				updateCartEmptyClass,
				updateCartWidgetCount,
				updateCartTotals,
				updateAppliedCouponsDisplay
			});
		} catch (error) {
			console.error('Failed to load Announcement Bar module:', error);
		}
	}

	// Get cart store - server-side state should already be populated via wp_interactivity_state
	const cartStore = store('caddy/cart');

	// Check if we already have server-rendered cart items
	const hasServerState = cartStore.state.items && cartStore.state.items.length > 0;

	// Update widget count with server state
	updateCartWidgetCount(cartStore.state.cartCount);

	// Clear any localStorage/sessionStorage that might interfere
	try {
		localStorage.removeItem('caddy_cart_update');
		localStorage.removeItem('caddy_last_cart_update');
		localStorage.removeItem('wc_cart_hash');
		// Clear WooCommerce fragments cache
		if (window.wc_cart_fragments_params && window.wc_cart_fragments_params.fragment_name) {
			sessionStorage.removeItem(window.wc_cart_fragments_params.fragment_name);
		}
	} catch (e) {
	}

	// Only fetch from Store API if we don't already have server-rendered cart data
	if (!hasServerState) {
		// Load initial cart data from Store API
		try {
			const storeApiNonce = document.querySelector('meta[name="wc-store-api-nonce"]')?.getAttribute('content');
			const cartResponse = await fetch('/wp-json/wc/store/v1/cart', {
				headers: storeApiNonce ? { 'Nonce': storeApiNonce } : {}
			});

			if (cartResponse.ok) {
				const cartData = await cartResponse.json();
				initialCartLoadComplete = true;

			// Update cart state with Store API data
			const cartStore = store('caddy/cart');
			if (cartData.items && cartData.items.length > 0) {
				// Convert Store API format to Caddy format
				const caddyItems = convertCartItems(cartData.items);

				cartStore.state.items = caddyItems;
				cartStore.state.cartCount = cartData.items_count;
				cartStore.state.cartTotal = cartData.totals.total_formatted || formatPrice(convertStoreApiPrice(cartData.totals.total_price));
				cartStore.state.cartSubtotal = getStoreApiSubtotal(cartStore.state, cartData);
				cartStore.state.cartSubtotalDisplay = formatPriceSmart(cartStore.state.cartSubtotal);
				cartStore.state.cartSubtotalFormatted = formatPrice(cartStore.state.cartSubtotal);
				cartStore.state.coupons = cartData.coupons || [];
				cartStore.state.discountTotal = getStoreApiDiscountTotal(cartStore.state, cartData);


				// Update widget counts and compass count
				updateCartWidgetCount(cartStore.state.cartCount);
				updateSavedItemsWidgetCount(cartStore.state.savedItems.length);

				// Update applied coupons display
				updateAppliedCouponsDisplay(cartData.coupons || []);

				// Update DOM elements with proper CSS classes based on sale status
				setTimeout(() => updateCartItemSaleClasses(caddyItems), 100);

			// Dispatch cart updated event for modules (rewards meter, etc.)
			document.dispatchEvent(new CustomEvent('caddy_cart_updated', { detail: { cartData } }));

				} else {
				// Empty cart - still dispatch event so modules can update
				document.dispatchEvent(new CustomEvent('caddy_cart_updated', { detail: { cartData } }));
			}
		}
		} catch (error) {
		}
	} else {
		// We have server-rendered state, mark initial load as complete
		initialCartLoadComplete = true;
		// Dispatch event so modules know cart is ready
		document.dispatchEvent(new CustomEvent('caddy_cart_updated', {
			detail: {
				cartData: {
					items: cartStore.state.items,
					items_count: cartStore.state.cartCount
				}
			}
		}));
	}
});


/**
 * Update cart item visibility for sale elements after state changes
 * @param {string} cartKey The cart key of the item to update
 * @param {Object} item The updated cart item data
 */
function updateCartItemVisibility(cartKey, item) {

	// Find the cart item container for this specific item
	const cartItemContainers = document.querySelectorAll('.cc-cart-item');
	let targetContainer = null;

	// Find the container that matches this cart key
	cartItemContainers.forEach((container) => {
		const containerCartKey = container.getAttribute('data-wp-key');
		if (containerCartKey === cartKey) {
			targetContainer = container;
		}
	});

	if (!targetContainer) {
		return;
	}

	// Find sale-related elements in this container
	const saleWrapper = targetContainer.querySelector('.cc-sale-price-wrapper');
	const savingsDiv = targetContainer.querySelector('.cc_saved_amount');

	// Update visibility based on item sale status
	if (saleWrapper) {
		if (item.showSalePrice) {
			saleWrapper.classList.remove('cc-hidden');
		} else {
			saleWrapper.classList.add('cc-hidden');
		}
	}

	if (savingsDiv) {
		if (item.showSavings) {
			savingsDiv.classList.remove('cc-hidden');
		} else {
			savingsDiv.classList.add('cc-hidden');
		}
	}
}

/**
 * Update cart item DOM elements to show/hide sale elements based on actual sale status
 * @param {Array} cartItems Array of cart items with pricing info
 */
function updateCartItemSaleClasses(cartItems) {

	// Find all cart item containers
	const cartItemContainers = document.querySelectorAll('.cc-cart-item');

	cartItemContainers.forEach((container, index) => {
		if (index >= cartItems.length) return;

		const item = cartItems[index];

		// Find sale-related elements in this container
		const saleWrapper = container.querySelector('.cc-sale-price-wrapper');
		const savingsDiv = container.querySelector('.cc_saved_amount');

		// Show or hide sale elements based on actual sale status
		const isActuallyOnSale = item.isOnSale && item.savingsPercentage > 0;

		if (saleWrapper) {
			if (isActuallyOnSale) {
				saleWrapper.style.display = '';
			} else {
				saleWrapper.style.display = 'none';
			}
		}

		if (savingsDiv) {
			if (isActuallyOnSale) {
				savingsDiv.style.display = '';
			} else {
				savingsDiv.style.display = 'none';
			}
		}
	});
}


/**
 * Vanilla JavaScript slideUp animation (jQuery-like)
 */
function slideUp(element, duration = 300, callback) {
	element.style.transitionProperty = 'height, margin, padding';
	element.style.transitionDuration = duration + 'ms';
	element.style.boxSizing = 'border-box';
	element.style.height = element.offsetHeight + 'px';
	element.offsetHeight; // Force reflow

	element.style.overflow = 'hidden';
	element.style.height = 0;
	element.style.paddingTop = 0;
	element.style.paddingBottom = 0;
	element.style.marginTop = 0;
	element.style.marginBottom = 0;

	window.setTimeout(() => {
		element.style.display = 'none';
		element.style.removeProperty('height');
		element.style.removeProperty('padding-top');
		element.style.removeProperty('padding-bottom');
		element.style.removeProperty('margin-top');
		element.style.removeProperty('margin-bottom');
		element.style.removeProperty('overflow');
		element.style.removeProperty('transition-duration');
		element.style.removeProperty('transition-property');
		if (callback) callback();
	}, duration);
}

/**
 * Vanilla JavaScript slideDown animation (jQuery-like)
 */
function slideDown(element, duration = 300, callback) {
	element.style.removeProperty('display');
	let display = window.getComputedStyle(element).display;

	if (display === 'none') display = 'block';
	element.style.display = display;

	const height = element.offsetHeight;
	element.style.overflow = 'hidden';
	element.style.height = 0;
	element.style.paddingTop = 0;
	element.style.paddingBottom = 0;
	element.style.marginTop = 0;
	element.style.marginBottom = 0;
	element.offsetHeight; // Force reflow

	element.style.boxSizing = 'border-box';
	element.style.transitionProperty = 'height, margin, padding';
	element.style.transitionDuration = duration + 'ms';
	element.style.height = height + 'px';
	element.style.removeProperty('padding-top');
	element.style.removeProperty('padding-bottom');
	element.style.removeProperty('margin-top');
	element.style.removeProperty('margin-bottom');

	window.setTimeout(() => {
		element.style.removeProperty('height');
		element.style.removeProperty('overflow');
		element.style.removeProperty('transition-duration');
		element.style.removeProperty('transition-property');
		if (callback) callback();
	}, duration);
}

/**
 * Initialize coupon drawer toggle functionality
 */
function initializeCouponDrawer() {

	// Handle coupon drawer toggle
	document.addEventListener('click', function(e) {
		// Check if clicked element is the coupon title or within it
		const couponTitle = e.target.closest('.cc-coupon-title');

		if (couponTitle) {
			e.preventDefault();

			const couponForm = document.querySelector('.cc-coupon-form');
			const couponWrapper = document.querySelector('.cc-coupon');

			if (couponForm && couponWrapper) {
				// Toggle using jQuery-like slideDown/slideUp approach
				const isHidden = window.getComputedStyle(couponForm).display === 'none';

				if (isHidden) {
					// Open the drawer
					couponWrapper.classList.add('cc-coupon-open');
					slideDown(couponForm, 300);
				} else {
					// Close the drawer
					slideUp(couponForm, 300, function() {
						couponWrapper.classList.remove('cc-coupon-open');
					});
				}
			}
		}
	});

	// Handle error notice close button (if there's an X or close area)
	document.addEventListener('click', function(e) {
		if (e.target.matches('.cc-coupon .woocommerce-error')) {
			const error = e.target;
			const clickX = e.pageX - error.getBoundingClientRect().left;

			// If clicked near the right edge (last 40px), close the error
			if (clickX > error.offsetWidth - 40) {
				const wrapper = error.closest('.woocommerce-notices-wrapper');
				if (wrapper) {
					wrapper.style.transition = 'opacity 200ms ease';
					wrapper.style.opacity = '0';
					setTimeout(() => {
						wrapper.style.display = 'none';
					}, 200);
				}
			}
		}
	});

}

/**
 * Initialize coupon form functionality with Store API
 */
function initializeCouponForm() {

	// Handle coupon form submission
	document.addEventListener('submit', async function(e) {
		if (e.target && e.target.id === 'apply_coupon_form') {
			e.preventDefault();

			const couponInput = document.getElementById('cc_coupon_code');
			const couponCode = couponInput ? couponInput.value.trim() : '';

			if (!couponCode) {
				return;
			}

			await applyCoupon(couponCode);
		}
	});

	// Handle coupon removal
	document.addEventListener('click', async function(e) {
		const removeButton = e.target.closest('.cc-remove-coupon');
		if (removeButton) {
			e.preventDefault();

			const couponElement = removeButton.closest('.cc-applied-coupon');
			const couponCode = couponElement ? couponElement.querySelector('.cc_applied_code').textContent.trim() : '';

			if (couponCode) {
				await removeCoupon(couponCode);
			}
		}
	});

}

/**
 * Update the applied coupons display in the DOM
 * @param {Array} coupons - Array of applied coupons from Store API
 */
function updateAppliedCouponsDisplay(coupons) {

	let discountsContainer = document.querySelector('.cc-discounts');

	if (discountsContainer) {
	}

	if (!discountsContainer) {
		// If container doesn't exist and we have coupons, create it
		if (coupons.length > 0) {
			createDiscountsContainer(coupons);
			// Update the savings display after creating the container
			updateCouponSavingsDisplay(coupons);
		}
		return;
	}

	if (coupons.length === 0) {
		// No coupons applied, hide the entire discounts container
		discountsContainer.style.display = 'none';
		return;
	}

	// Show the discounts container and update the coupons list
	discountsContainer.style.display = '';
	const discountDiv = discountsContainer.querySelector('.cc-discount');
	if (discountDiv) {
		// Clear existing coupons
		discountDiv.innerHTML = '';

		// Add each applied coupon
		coupons.forEach(coupon => {
			const couponElement = createCouponElement(coupon.code);
			discountDiv.appendChild(couponElement);
		});
	}

	// Update the savings amount if available
	updateCouponSavingsDisplay(coupons);
}

/**
 * Create the discounts container if it doesn't exist
 * @param {Array} coupons - Array of applied coupons
 */
function createDiscountsContainer(coupons) {
	// Find the totals section (cc-totals) to insert the discounts before it
	const totalsSection = document.querySelector('.cc-totals');

	if (totalsSection) {
		// Insert before totals section for correct placement (after coupon, before subtotal/checkout)
		const discountsHTML = `
			<div class="cc-discounts">
				<div class="cc-discount">
					${coupons.map(coupon => createCouponHTML(coupon.code)).join('')}
				</div>
				<div class="cc-savings"></div>
			</div>
		`;
		totalsSection.insertAdjacentHTML('beforebegin', discountsHTML);
	} else {
		// Fallback to after coupon section if totals not found
		const couponSection = document.querySelector('.cc-coupon');
		if (!couponSection) return;

		const discountsHTML = `
			<div class="cc-discounts">
				<div class="cc-discount">
					${coupons.map(coupon => createCouponHTML(coupon.code)).join('')}
				</div>
				<div class="cc-savings"></div>
			</div>
		`;
		couponSection.insertAdjacentHTML('afterend', discountsHTML);
	}
}

/**
 * Create HTML for a single coupon element
 * @param {string} code - Coupon code
 * @returns {string} HTML string
 */
function createCouponHTML(code) {
	const pluginDir = window.caddyConfig?.pluginDir || '/wp-content/plugins/caddy-premium/';
	return `
		<div class="cc-applied-coupon">
			<img src="${pluginDir}public/assets/img/tag-icon.svg" alt="Discount Code">
			<span class="cc_applied_code">${code}</span>
			<a href="javascript:void(0);" class="cc-remove-coupon"><i class="ccicon-close"></i></a>
		</div>
	`;
}

/**
 * Create a DOM element for a single coupon
 * @param {string} code - Coupon code
 * @returns {HTMLElement} Coupon element
 */
function createCouponElement(code) {
	const couponDiv = document.createElement('div');
	couponDiv.className = 'cc-applied-coupon';
	couponDiv.innerHTML = createCouponHTML(code);
	return couponDiv.firstElementChild;
}

/**
 * Update the savings display for applied coupons
 * @param {Array} coupons - Array of applied coupons
 */
function updateCouponSavingsDisplay(coupons) {
	const savingsContainer = document.querySelector('.cc-savings');
	if (!savingsContainer) {
		return;
	}

	// Get the current state to access totals
	const { state } = store('caddy/cart');

	// Use stored discount total if available, otherwise try to calculate
	let discount = state.discountTotal || 0;


	// If no stored discount, calculate by comparing original subtotal with current total
	if (discount === 0 && state.items) {
		const originalSubtotal = state.items.reduce((total, item) => total + (item.lineTotal || 0), 0);
		const currentTotal = state.cartSubtotal || 0;
		discount = originalSubtotal - currentTotal;
	}

	// Display the savings if there's a discount
	if (discount > 0) {
		savingsContainer.innerHTML = `<span class="cc-discount-amount">-${formatPrice(discount)}</span>`;
	} else {
		savingsContainer.innerHTML = '';
	}
}

/**
 * Apply a coupon using the Store API
 * @param {string} couponCode - The coupon code to apply
 */
async function applyCoupon(couponCode) {

	const cartContainer = document.querySelector('#cc-cart');

	// Show loading state
	if (cartContainer) {
		cartContainer.style.opacity = '0.3';
	}

	// Clear any existing notices
	const noticesWrapper = document.querySelector('.cc-coupon .woocommerce-notices-wrapper');
	if (noticesWrapper) {
		noticesWrapper.innerHTML = '';
	}

	try {
		// Get the Store API nonce
		const storeApiNonce = document.querySelector('meta[name="wc-store-api-nonce"]')?.getAttribute('content');

		const response = await fetch('/wp-json/wc/store/v1/cart/apply-coupon', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				...(storeApiNonce ? { 'Nonce': storeApiNonce } : {})
			},
			credentials: 'same-origin',
			body: JSON.stringify({
				code: couponCode
			})
		});

		const data = await response.json();

		if (!response.ok) {
			const { state } = store('caddy/cart');
			throw new Error(data.message || state.i18n.errorApplyCouponFailed);
		}


		// Update only the totals and pricing - don't touch cart items
		const { state } = store('caddy/cart');

		// Update totals using centralized helper function
		updateCartTotals(state, data);
		state.coupons = data.coupons || [];
		// Store discount amount if available (or clear it when removing coupons)
		state.discountTotal = getStoreApiDiscountTotal(state, data);

		// Update applied coupons display
		updateAppliedCouponsDisplay(data.coupons || []);

		// Dispatch cart update event for premium features (rewards meter, announcement bar, etc.)
		document.dispatchEvent(new CustomEvent('caddy_cart_updated', { detail: { cartData: data } }));

		// Clear the input field
		const couponInput = document.getElementById('cc_coupon_code');
		if (couponInput) {
			couponInput.value = '';
		}

		// Close the coupon drawer
		const couponForm = document.querySelector('.cc-coupon-form');
		const couponWrapper = document.querySelector('.cc-coupon');
		if (couponForm && couponWrapper) {
			slideUp(couponForm, 300, function() {
				couponWrapper.classList.remove('cc-coupon-open');
			});
		}

		// Show success message (optional)

	} catch (error) {

		// Show error message
		if (noticesWrapper) {
			const { state } = store('caddy/cart');
			noticesWrapper.innerHTML = `
				<div class="woocommerce-error" role="alert">
					${error.message || state.i18n.errorApplyCouponTryAgain}
				</div>
			`;
		}
	} finally {
		// Remove loading state
		if (cartContainer) {
			cartContainer.style.opacity = '1';
		}
	}
}

/**
 * Remove a coupon using the Store API
 * @param {string} couponCode - The coupon code to remove
 */
async function removeCoupon(couponCode) {

	const cartContainer = document.querySelector('#cc-cart');

	// Show loading state
	if (cartContainer) {
		cartContainer.style.opacity = '0.3';
	}

	try {
		// Get the Store API nonce
		const storeApiNonce = document.querySelector('meta[name="wc-store-api-nonce"]')?.getAttribute('content');

		const response = await fetch('/wp-json/wc/store/v1/cart/remove-coupon', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				...(storeApiNonce ? { 'Nonce': storeApiNonce } : {})
			},
			credentials: 'same-origin',
			body: JSON.stringify({
				code: couponCode
			})
		});

		const data = await response.json();

		if (!response.ok) {
			const { state } = store('caddy/cart');
			throw new Error(data.message || state.i18n.errorRemoveCouponFailed);
		}


		// Update only the totals and pricing - don't touch cart items
		const { state } = store('caddy/cart');

		// Update totals using centralized helper function
		updateCartTotals(state, data);
		state.coupons = data.coupons || [];
		// Store discount amount if available (or clear it when removing coupons)
		state.discountTotal = getStoreApiDiscountTotal(state, data);

		// Update applied coupons display
		updateAppliedCouponsDisplay(data.coupons || []);

		// Dispatch cart update event for premium features (rewards meter, announcement bar, etc.)
		document.dispatchEvent(new CustomEvent('caddy_cart_updated', { detail: { cartData: data } }));

	} catch (error) {

		// Show error message
		const noticesWrapper = document.querySelector('.cc-coupon .woocommerce-notices-wrapper');
		if (noticesWrapper) {
			const { state } = store('caddy/cart');
			noticesWrapper.innerHTML = `
				<div class="woocommerce-error" role="alert">
					${error.message || state.i18n.errorRemoveCouponTryAgain}
				</div>
			`;
		}
	} finally {
		// Remove loading state
		if (cartContainer) {
			cartContainer.style.opacity = '1';
		}
	}
}

/**
 * --------------------------------------------------
 * Tab Switching Functions
 * --------------------------------------------------
 */

/**
 * Initialize tab navigation
 */
function initTabs() {
	const tabLinks = document.querySelectorAll('[data-tabs] a');

	if (!tabLinks.length) {
		return;
	}

	tabLinks.forEach(link => {
		link.addEventListener('click', function(e) {
			e.preventDefault();
			const targetId = this.getAttribute('href');
			switchToTab(targetId);
		});
	});
}

/**
 * Switch to a specific tab
 * @param {string} tabId - The tab ID to switch to (e.g., '#cc-cart', '#cc-saves', '#cc-rewards')
 */
function switchToTab(tabId) {
	// Remove active classes from all tabs
	document.querySelectorAll('.cc-screen-tab').forEach(tab => {
		tab.classList.remove('cc-active');
		tab.classList.remove('cc-tab-active');
	});

	// Remove active state from all tab links
	document.querySelectorAll('[data-tabs] a').forEach(link => {
		link.classList.remove('active');
		link.setAttribute('aria-selected', 'false');
	});

	// Add active class to target tab
	const targetTab = document.querySelector(tabId);
	if (targetTab) {
		// Use cc-tab-active for non-cart tabs, cc-active for cart
		if (tabId === '#cc-cart') {
			targetTab.classList.add('cc-active');
		} else {
			targetTab.classList.add('cc-tab-active');
		}
	}

	// Add active state to corresponding link
	const targetLink = document.querySelector(`[data-tabs] a[href="${tabId}"]`);
	if (targetLink) {
		targetLink.classList.add('active');
		targetLink.setAttribute('aria-selected', 'true');
	}

	// Update header title and back button icon based on active tab
	const headerTitle = document.querySelector('.cc-header-title');
	const closeIcon = document.querySelector('.cc-header-back-close');
	const arrowIcon = document.querySelector('.cc-header-back-arrow');
	const countSpan = document.querySelector('.cc-header-title .cc-cart-tab-count');

	if (headerTitle) {
		// Store original content on first switch
		if (!headerTitle._originalContent) {
			headerTitle._originalContent = headerTitle.cloneNode(true);
		}

		if (tabId === '#cc-cart') {
			// Restore original "Your Cart (N)" content
			headerTitle.replaceChildren(...headerTitle._originalContent.cloneNode(true).childNodes);
		} else {
			const { state: i18nState } = store('caddy/cart');
			const titles = {
				'#cc-saves': i18nState?.i18n?.yourSaves || 'Your Saves',
				'#cc-offers': i18nState?.i18n?.yourOffers || 'Your Offers',
			};
			headerTitle.textContent = titles[tabId] || i18nState?.i18n?.yourCart || 'Your Cart';
		}
	}

	// Toggle X (close) vs ← (back) icon
	if (closeIcon && arrowIcon) {
		if (tabId === '#cc-cart') {
			closeIcon.style.display = '';
			arrowIcon.style.display = 'none';
		} else {
			closeIcon.style.display = 'none';
			arrowIcon.style.display = '';
		}
	}
}

/**
 * Handle free gift UI - Remove quantity controls, SFL button, and trash icon from DOM
 */
function handleFreeGiftUI() {
	const cartState = store('caddy/cart');
	if (!cartState || !cartState.state || !cartState.state.items) {
		return;
	}

	// Get all cart items
	const items = cartState.state.items;

	// Loop through each item
	items.forEach(item => {
		if (item.isFreeGift || item.isBundledItem) {
			// Find the cart item element by cart key
			const cartItemEl = document.querySelector(`[data-wp-key="${item.cartKey}"]`);
			if (!cartItemEl) {
				return;
			}

			// Remove quantity controls
			const qtyWrap = cartItemEl.querySelector('.cc_item_quantity_wrap');
			if (qtyWrap) {
				qtyWrap.remove();
			}

			// Remove Save for Later button
			const sflBtn = cartItemEl.querySelector('.cc_sfl_btn');
			if (sflBtn) {
				sflBtn.remove();
			}

			// Remove trash icon (remove button)
			const removeBtn = cartItemEl.querySelector('.cc-remove-item');
			if (removeBtn) {
				removeBtn.remove();
			}
		}
	});
}

// Listen for cart updates and handle free gift UI
document.addEventListener('caddy_cart_updated', () => {
	// Use setTimeout to ensure DOM has been updated by Interactivity API
	setTimeout(handleFreeGiftUI, 50);
});

// Hydrate subscription schemes for items added client-side
document.addEventListener('caddy_cart_updated', () => {
	const cartStore = store('caddy/cart');
	if (cartStore?.state) {
		hydrateSubscriptionSchemes(cartStore.state);
	}
});

// Initialize tabs when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
	initTabs();
	// Handle free gifts on initial load
	handleFreeGiftUI();
});

// Expose tab switching globally for use by other modules
window._caddySwitchToCartTab = function() {
	switchToTab('#cc-cart');
};

window._caddySwitchToTab = switchToTab;

// Export core functions for optional modules
export {
	updateCartTotalsFromItems,
	removeItemFromServer,
	refreshCartFromServer,
	updateCartEmptyClass,
	updateCartWidgetCount,
	updateCartTotals,
	updateAppliedCouponsDisplay,
	initializeRecommendations
};

// Expose refreshCartFromServer globally for premium features
window._caddyRefreshCart = refreshCartFromServer;
